// 🌐 API Service for Database Operations
// Handles all GraphQL API calls to the database operations service

import { adminApiUrl } from '../utils/adminApiBase';
import { disconnectAdminRealtime } from './realtime';
import { log } from '../utils/log';
import { publicSiteOrigin } from '../utils/publicSiteUrl';
import { clearAdminStaffTokenCookie } from '../utils/adminStaffTokenCookie';
import { getBffInternalAuthHeaders } from '../utils/bffInternalAuth';

const API_BASE_URL = 'http://localhost:3012/index.php';

/** BFF base URL — dev uses Vite proxy; deployed admin uses same-origin `/api/v1` (nginx → BFF). */
function resolveBffBaseUrl() {
  const fromEnv =
    typeof import.meta !== 'undefined' && import.meta.env?.VITE_BFF_API_URL
      ? String(import.meta.env.VITE_BFF_API_URL).replace(/\/$/, '')
      : '';

  if (fromEnv) return fromEnv;

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      // Vite dev & preview proxy /api/v1 → BFF (port 4001).
      return '/api/v1';
    }
    // Staging/production: nginx on admin host proxies /api/v1 → BFF
    return '/api/v1';
  }

  if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
    return '/api/v1';
  }

  return 'http://localhost:4001/api/v1';
}

function getBffBaseUrl() {
  return resolveBffBaseUrl();
}

const CSRF_COOKIE = 'csrf_token';
const CSRF_HEADER = 'X-CSRF-Token';

function readCsrfCookie() {
  try {
    if (typeof document === 'undefined') return '';
    const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

let csrfBootstrapPromise = null;

async function ensureCsrfToken() {
  let token = readCsrfCookie();
  if (token) return token;
  if (!csrfBootstrapPromise) {
    csrfBootstrapPromise = fetch(`${getBffBaseUrl()}/csrf-token`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    })
      .then(async (res) => {
        if (!res.ok) return '';
        const body = await res.json().catch(() => ({}));
        return readCsrfCookie() || String(body?.csrfToken || '');
      })
      .catch(() => '')
      .finally(() => {
        csrfBootstrapPromise = null;
      });
  }
  token = await csrfBootstrapPromise;
  return token || readCsrfCookie();
}

function bffNetworkErrorMessage(path, err) {
  const bffBase = getBffBaseUrl();
  const base = bffBase.startsWith('http')
    ? bffBase
    : `${typeof window !== 'undefined' ? window.location.origin : ''}${bffBase}`;
  return (
    `Cannot reach the API (${base}${path}). ` +
    'Start the BFF service (default port 4001), e.g. `cd services/bff-service && npm run dev`. ' +
    `Details: ${err?.message || 'network error'}`
  );
}

class ApiService {
  /** REST calls to BFF (fleet vehicles) */
  static adminBearerHeaders() {
    try {
      if (typeof sessionStorage === 'undefined') return {};
      const token = sessionStorage.getItem('admin-staff-jwt');
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
      return {};
    }
  }

  /** True when BFF rejected the admin JWT (session should be cleared). */
  static isBffUnauthorized(status, body, _path) {
    if (status === 401 || status === 403) return true;
    const code = String(body?.code || body?.error_code || '').toUpperCase();
    if (code === 'UNAUTHORIZED' || code === 'FORBIDDEN') return true;
    const msg = String(body?.message || body?.error || '').toLowerCase();
    return (
      msg.includes('unauthorized') ||
      msg.includes('authentication required') ||
      msg.includes('invalid token') ||
      msg.includes('jwt')
    );
  }

  /** Clear admin staff session after BFF auth failure. */
  static clearAdminSession() {
    try {
      disconnectAdminRealtime();
    } catch {
      /* ignore */
    }
    try {
      localStorage.setItem('admin-signed-out', '1');
      sessionStorage.removeItem('admin-staff-jwt');
      sessionStorage.removeItem('admin-staff-user-id');
      clearAdminStaffTokenCookie();
    } catch {
      /* ignore storage errors */
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('admin:session-cleared'));
    }
  }

  /** Vite-proxied calls to booking-service SQLite admin API (port 3002). */
  static adminSqliteFetch(url, options = {}) {
    const { headers: optHeaders, ...rest } = options;
    return fetch(url, {
      ...rest,
      headers: {
        ...this.adminBearerHeaders(),
        ...(optHeaders || {}),
      },
    });
  }

  static async bffRequest(path, options = {}) {
    const url = `${getBffBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;
    const { headers: optHeaders, ...rest } = options;
    const isFormData = typeof FormData !== 'undefined' && rest.body instanceof FormData;
    const method = String(rest.method || 'GET').toUpperCase();
    const needsCsrf = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
    const csrfToken = needsCsrf ? await ensureCsrfToken() : '';
    const fetchOpts = {
      ...rest,
      credentials: 'include',
      cache: 'no-store',
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        ...getBffInternalAuthHeaders(),
        ...this.adminBearerHeaders(),
        ...(csrfToken ? { [CSRF_HEADER]: csrfToken } : {}),
        ...(optHeaders || {}),
      },
    };
    let res;
    try {
      res = await fetch(url, fetchOpts);
    } catch (err) {
      throw new Error(bffNetworkErrorMessage(path, err));
    }
    if (res.status === 304) {
      res = await fetch(url, {
        ...fetchOpts,
        headers: { ...fetchOpts.headers, 'If-None-Match': '', 'If-Modified-Since': '' },
      });
    }
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (String(body.code || body.error || '').toUpperCase() === 'ADMIN_IP_FORBIDDEN') {
        this.clearAdminSession();
        const ipHint = body.client_ip ? ` (your IP: ${body.client_ip})` : '';
        throw new Error(
          `${body.message || 'Admin panel access is restricted to approved IP addresses.'}${ipHint}`,
        );
      }
      if (this.isBffUnauthorized(res.status, body, path)) {
        this.clearAdminSession();
      }
      const msg =
        body.message ||
        res.statusText ||
        (res.status === 504
          ? 'Gateway timed out (504). The API was still working when the proxy gave up.'
          : 'BFF request failed');
      const detail = body.error && body.error !== msg ? `${msg}: ${body.error}` : msg;
      const pathHint = body.path ? ` — ${body.path}` : '';
      const issuesHint =
        Array.isArray(body.issues) && body.issues.length
          ? ` (${body.issues.join('; ')})`
          : '';
      throw new Error(`${detail}${pathHint}${issuesHint}`);
    }
    return body;
  }

  static async getFleetVehicles(params = {}) {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.available === true) q.set('available', 'true');
    q.set('limit', String(params.limit || 500));
    q.set('offset', String(params.offset || 0));
    const qs = q.toString();
    return this.bffRequest(`/vehicles${qs ? `?${qs}` : ''}`);
  }

  static async createFleetVehicle(input) {
    return this.bffRequest('/vehicles', { method: 'POST', body: JSON.stringify(input) });
  }

  static async updateFleetVehicle(id, input) {
    return this.bffRequest(`/vehicles/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  }

  static async deleteFleetVehicle(id) {
    return this.bffRequest(`/vehicles/${id}`, { method: 'DELETE' });
  }

  // Enhanced Fleet Management API Methods
  static async getEnhancedFleetVehicles(params = {}) {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.brand) q.set('brand', params.brand);
    if (params.type) q.set('type', params.type);
    if (params.fuel_type) q.set('fuel_type', params.fuel_type);
    if (params.documents_verified !== undefined) q.set('documents_verified', params.documents_verified);
    if (params.expiry_alert) q.set('expiry_alert', params.expiry_alert);
    q.set('limit', String(params.limit || 500));
    q.set('offset', String(params.offset || 0));
    const qs = q.toString();
    return this.bffRequest(`/fleet${qs ? `?${qs}` : ''}`);
  }

  static async listCabApprovals(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && String(v).trim() !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/fleet/approvals${qs ? `?${qs}` : ''}`);
  }

  static async approveCab(id, body = {}) {
    return this.bffRequest(`/fleet/approvals/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async rejectCab(id, body = {}) {
    return this.bffRequest(`/fleet/approvals/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async listDriverApprovals(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && String(v).trim() !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/drivers/approvals${qs ? `?${qs}` : ''}`);
  }

  static async approveDriver(id, body = {}) {
    return this.bffRequest(`/drivers/approvals/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async rejectDriver(id, body = {}) {
    return this.bffRequest(`/drivers/approvals/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async createEnhancedFleetVehicle(formData) {
    // Use FormData for file uploads
    return this.bffRequest('/fleet', {
      method: 'POST',
      headers: {
        // Remove Content-Type header for FormData to set boundary automatically
      },
      body: formData
    });
  }

  static async updateEnhancedFleetVehicle(id, formData) {
    return this.bffRequest(`/fleet/${id}`, {
      method: 'PUT',
      headers: {
        // Remove Content-Type header for FormData to set boundary automatically
      },
      body: formData
    });
  }

  static async deleteEnhancedFleetVehicle(id) {
    return this.bffRequest(`/fleet/${id}`, { method: 'DELETE' });
  }

  static async getFleetExpiringDocuments(days = 30) {
    return this.bffRequest(`/fleet/expiring-documents?days=${days}`);
  }

  static async getFleetStats() {
    return this.bffRequest('/fleet/stats');
  }

  /** Watch Tower REST API (`/api/v1/watchtower/*`). Realtime via Socket.IO — see getWatchtowerRealtimeSchema. */
  static async getWatchtowerHealth() {
    return this.bffRequest('/watchtower/health');
  }

  static async getWatchtowerSnapshot() {
    return this.bffRequest('/watchtower/snapshot');
  }

  static async getWatchtowerRealtimeSchema() {
    return this.bffRequest('/watchtower/realtime/schema');
  }

  static async getWatchtowerFleetLocations() {
    return this.bffRequest('/watchtower/fleet-locations');
  }

  static async ingestWatchtowerTelemetry(payload) {
    return this.bffRequest('/watchtower/telemetry/ingest', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async getWatchtowerOpsServices() {
    return this.bffRequest('/watchtower/ops/services');
  }

  static async getWatchtowerOpsApiPerformance({ limit = 100, slowOnly = false } = {}) {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (slowOnly) qs.set('slow_only', 'true');
    return this.bffRequest(`/watchtower/ops/api-performance?${qs}`);
  }

  static async getWatchtowerOpsErrorLogs({ limit = 100 } = {}) {
    return this.bffRequest(`/watchtower/ops/error-logs?limit=${limit}`);
  }

  static async getWatchtowerOpsOperationsTable({ limit = 200, offset = 0 } = {}) {
    return this.bffRequest(`/watchtower/ops/operations-table?limit=${limit}&offset=${offset}`);
  }

  static async restartWatchtowerService(serviceId) {
    return this.bffRequest(`/watchtower/ops/services/${encodeURIComponent(serviceId)}/restart`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async getWatchtowerFailedTransactions({ limit = 100, kind, search } = {}) {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (kind) qs.set('kind', kind);
    if (search && String(search).trim()) qs.set('search', String(search).trim());
    return this.bffRequest(`/watchtower/ops/failed-transactions?${qs}`);
  }

  static async uploadFleetDocument(vehicleId, documentType, file) {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('vehicleId', vehicleId);
    formData.append('documentType', documentType);

    return this.bffRequest('/fleet/upload-document', {
      method: 'POST',
      headers: {
        // Remove Content-Type header for FormData to set boundary automatically
      },
      body: formData
    });
  }

  // Helper method to create FormData from vehicle form data
  static createVehicleFormData(vehicleData, files = {}) {
    const formData = new FormData();

    // Add all text fields
    Object.keys(vehicleData).forEach(key => {
      if (vehicleData[key] !== null && vehicleData[key] !== undefined && vehicleData[key] !== '') {
        formData.append(key, vehicleData[key]);
      }
    });

    // Add files
    Object.keys(files).forEach(fieldName => {
      if (files[fieldName]) {
        formData.append(fieldName, files[fieldName]);
      }
    });

    return formData;
  }

  // Helper method to create FormData from driver form data
  static createDriverFormData(driverData) {
    const formData = new FormData();

    // Add all text fields
    Object.keys(driverData).forEach(key => {
      if (key === 'dl_image' || key === 'driver_image') {
        // Handle file fields separately
        if (driverData[key] && driverData[key] instanceof File) {
          formData.append(key, driverData[key]);
        }
      } else if (key === 'languages_known') {
        // Handle array fields
        if (Array.isArray(driverData[key])) {
          formData.append(key, JSON.stringify(driverData[key]));
        }
      } else if (driverData[key] !== null && driverData[key] !== undefined && driverData[key] !== '') {
        formData.append(key, driverData[key]);
      }
    });

    return formData;
  }

  static async getFleetDrivers(params = {}) {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.available === true) q.set('available', 'true');
    q.set('limit', String(params.limit || 500));
    q.set('offset', String(params.offset || 0));
    const qs = q.toString();
    return this.bffRequest(`/drivers${qs ? `?${qs}` : ''}`);
  }

  static async createFleetDriver(input) {
    if (input?.dl_image || input?.driver_image) {
      const formData = new FormData();
      Object.entries(input || {}).forEach(([key, value]) => {
        if (value == null || value === '') return;
        if ((key === 'dl_image' || key === 'driver_image') && value instanceof File) {
          formData.append(key, value);
          return;
        }
        if (key === 'languages_spoken' && Array.isArray(value)) {
          formData.append(key, value.join(', '));
          return;
        }
        formData.append(key, String(value));
      });
      return this.bffRequest('/drivers', {
        method: 'POST',
        headers: {},
        body: formData,
      });
    }
    return this.bffRequest('/drivers', { method: 'POST', body: JSON.stringify(input) });
  }

  static async updateFleetDriver(id, input) {
    if (input?.dl_image || input?.driver_image) {
      const formData = new FormData();
      Object.entries(input || {}).forEach(([key, value]) => {
        if (value == null || value === '') return;
        if ((key === 'dl_image' || key === 'driver_image') && value instanceof File) {
          formData.append(key, value);
          return;
        }
        if (key === 'languages_spoken' && Array.isArray(value)) {
          formData.append(key, value.join(', '));
          return;
        }
        formData.append(key, String(value));
      });
      return this.bffRequest(`/drivers/${id}`, {
        method: 'PUT',
        headers: {},
        body: formData,
      });
    }
    return this.bffRequest(`/drivers/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  }

  static async deleteFleetDriver(id) {
    return this.bffRequest(`/drivers/${id}`, { method: 'DELETE' });
  }

  static async resetDriverDevice(id, body = {}) {
    return this.bffRequest(`/drivers/${id}/reset-device`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async suspendDriver(id, body = {}) {
    return this.bffRequest(`/drivers/${id}/suspend`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async requestBillCorrection(bookingId, body = {}) {
    return this.bffRequest(`/bookings/${bookingId}/bill-correction`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** BFF bookings list (admin) */
  static async adminBookingServiceRequest(path, options = {}) {
    const url = adminApiUrl(path.startsWith('/') ? path : `/${path}`);
    const { headers: optHeaders, ...rest } = options;
    const isFormData = typeof FormData !== 'undefined' && rest.body instanceof FormData;
    const res = await fetch(url, {
      ...rest,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...this.adminBearerHeaders(),
        ...(optHeaders || {}),
      },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = body.message || res.statusText || 'Admin API request failed';
      throw new Error(msg);
    }
    return body;
  }

  /** Booking-service journey leads (`/api/admin/leads`) */
  static async getLeads(params = {}) {
    const q = new URLSearchParams();
    q.set('page', String(params.page || 1));
    q.set('limit', String(Math.min(params.limit || 50, 200)));
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    if (params.fromCity) q.set('fromCity', params.fromCity);
    if (params.toCity) q.set('toCity', params.toCity);
    if (params.tripType) q.set('tripType', params.tripType);
    if (params.isConverted === true) q.set('isConverted', 'true');
    if (params.isConverted === false) q.set('isConverted', 'false');
    if (params.quoteOnly) q.set('quoteOnly', 'true');
    if (params.dateFrom) q.set('dateFrom', params.dateFrom);
    if (params.dateTo) q.set('dateTo', params.dateTo);
    if (params.quoteStage) q.set('quoteStage', params.quoteStage);
    return this.adminBookingServiceRequest(`/api/admin/leads?${q.toString()}`);
  }

  static async getLead(id) {
    return this.adminBookingServiceRequest(`/api/admin/leads/${encodeURIComponent(id)}`);
  }

  static async deleteLead(id) {
    return this.adminBookingServiceRequest(`/api/admin/leads/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  /** Marketing attribution (`/api/admin/marketing/*`) */
  static async getMarketingLeads(params = {}) {
    const q = new URLSearchParams();
    q.set('page', String(params.page || 1));
    q.set('limit', String(Math.min(params.limit || 50, 200)));
    if (params.search) q.set('search', String(params.search).trim());
    if (params.utmSource) q.set('utmSource', params.utmSource);
    if (params.utmMedium) q.set('utmMedium', params.utmMedium);
    if (params.utmCampaign) q.set('utmCampaign', params.utmCampaign);
    if (params.deviceType) q.set('deviceType', params.deviceType);
    if (params.isConverted === true) q.set('isConverted', 'true');
    if (params.isConverted === false) q.set('isConverted', 'false');
    if (params.dateFrom) q.set('dateFrom', params.dateFrom);
    if (params.dateTo) q.set('dateTo', params.dateTo);
    return this.adminBookingServiceRequest(`/api/admin/marketing/leads?${q.toString()}`);
  }

  static async exportMarketingLeads(params = {}) {
    const q = new URLSearchParams();
    if (params.search) q.set('search', String(params.search).trim());
    if (params.utmSource) q.set('utmSource', params.utmSource);
    if (params.isConverted) q.set('isConverted', params.isConverted);
    if (params.dateFrom) q.set('dateFrom', params.dateFrom);
    if (params.dateTo) q.set('dateTo', params.dateTo);
    const url = adminApiUrl(`/api/admin/marketing/leads/export?${q.toString()}`);
    const res = await fetch(url, { headers: this.adminBearerHeaders() });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'user-journey-funnels.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  static async getUtmTrackingDashboard(params = {}) {
    const q = new URLSearchParams();
    if (params.range) q.set('range', params.range);
    return this.bffRequest(`/admin/utm-tracking/dashboard?${q.toString()}`);
  }

  static async getUtmTrackingList(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    return this.bffRequest(`/admin/utm-tracking?${q.toString()}`);
  }

  static async getUtmTrackingById(id) {
    return this.bffRequest(`/admin/utm-tracking/${encodeURIComponent(id)}`);
  }

  static async deleteUtmTracking(id) {
    return this.bffRequest(`/admin/utm-tracking/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async generateUtmTrackingUrl(body) {
    return this.bffRequest('/admin/utm-tracking/generate-url', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  static async exportUtmTrackingCsv(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    const url = `${getBffBaseUrl()}/admin/utm-tracking/export${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'text/csv', ...this.adminBearerHeaders() },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Export failed');
    }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'utm-url-tracking.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  static async getMarketingCampaigns({ dateFrom, dateTo } = {}) {
    const q = new URLSearchParams();
    if (dateFrom) q.set('dateFrom', dateFrom);
    if (dateTo) q.set('dateTo', dateTo);
    return this.adminBookingServiceRequest(`/api/admin/marketing/campaigns?${q.toString()}`);
  }

  static async getMarketingSourceReport({ dateFrom, dateTo } = {}) {
    const q = new URLSearchParams();
    if (dateFrom) q.set('dateFrom', dateFrom);
    if (dateTo) q.set('dateTo', dateTo);
    return this.adminBookingServiceRequest(`/api/admin/marketing/source-report?${q.toString()}`);
  }

  static async getMarketingConversionReport({ dateFrom, dateTo } = {}) {
    const q = new URLSearchParams();
    if (dateFrom) q.set('dateFrom', dateFrom);
    if (dateTo) q.set('dateTo', dateTo);
    return this.adminBookingServiceRequest(`/api/admin/marketing/conversion-report?${q.toString()}`);
  }

  static async getMarketingRevenueReport({ dateFrom, dateTo } = {}) {
    const q = new URLSearchParams();
    if (dateFrom) q.set('dateFrom', dateFrom);
    if (dateTo) q.set('dateTo', dateTo);
    return this.adminBookingServiceRequest(`/api/admin/marketing/revenue-report?${q.toString()}`);
  }

  static async getMarketingVisitorJourney(leadId) {
    return this.adminBookingServiceRequest(
      `/api/admin/marketing/visitor-journey/${encodeURIComponent(leadId)}`
    );
  }

  static async getBookings(params = {}) {
    const q = new URLSearchParams();
    q.set('page', String(params.page || 1));
    q.set('limit', String(Math.min(params.limit || 500, 500)));
    if (params.status) q.set('status', params.status);
    if (params.search && String(params.search).trim().length >= 2) {
      q.set('search', String(params.search).trim());
    }
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);
    if (params.vendor_unassigned) q.set('vendor_unassigned', 'true');
    if (params.enrich_trip_metrics) q.set('enrich_trip_metrics', 'true');
    if (params.customer_id != null && params.customer_id !== '') {
      q.set('customer_id', String(params.customer_id));
    }
    return this.bffRequest(`/bookings?${q.toString()}`);
  }

  static async getBookingInvoices(params = {}) {
    const q = new URLSearchParams();
    q.set('page', String(params.page || 1));
    q.set('limit', String(Math.min(params.limit || 50, 500)));
    if (params.search && String(params.search).trim().length >= 2) {
      q.set('search', String(params.search).trim());
    }
    if (params.invoice_status) q.set('invoice_status', params.invoice_status);
    if (params.generated_only) q.set('generated_only', '1');
    return this.bffRequest(`/bookings/invoices?${q.toString()}`);
  }

  static async getPayments(params = {}) {
    const q = new URLSearchParams();
    q.set('page', String(params.page || 1));
    q.set('limit', String(Math.min(params.limit || 50, 200)));
    if (params.status) q.set('status', params.status);
    if (params.method) q.set('method', params.method);
    if (params.booking_id) q.set('booking_id', String(params.booking_id));
    if (params.search && String(params.search).trim().length >= 2) {
      q.set('search', String(params.search).trim());
    }
    return this.bffRequest(`/payments/transactions?${q.toString()}`);
  }

  static async refundPaymentTransaction(id, { amount, reason } = {}) {
    const body = {};
    if (amount != null && amount !== '') body.amount = amount;
    if (reason != null && String(reason).trim()) body.reason = String(reason).trim();
    return this.bffRequest(`/payments/transactions/${encodeURIComponent(id)}/refund`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async cancelBooking(id, reason = 'Cancelled from admin', opts = {}) {
    const body = {
      reason,
      cancelled_by: opts.cancelled_by || 'admin',
      skip_retention: opts.skip_retention !== false,
      policy_acknowledged: opts.policy_acknowledged !== false,
    };
    return this.bffRequest(`/bookings/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Single booking (admin) — same shape as list row plus joined fields */
  static async getBooking(id) {
    return this.bffRequest(`/bookings/${encodeURIComponent(id)}`);
  }

  /** Manual admin assign: POST /bookings/:id/assign-driver { driver_id, vehicle_id } */
  static async assignBookingDriver(bookingId, body = {}) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/assign-driver`, {
      method: 'POST',
      body: JSON.stringify({
        driver_id: Number(body.driver_id),
        vehicle_id: Number(body.vehicle_id),
      }),
    });
  }

  static async approveBookingAssignment(bookingId, body = {}) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/assignment-approval/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async rejectBookingAssignment(bookingId, body = {}) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/assignment-approval/reject`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async requestBookingReassignment(bookingId, body = {}) {
    return this.bffRequest(
      `/bookings/${encodeURIComponent(bookingId)}/assignment-approval/request-reassignment`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  }

  static async listPendingAssignmentApprovals(params = {}) {
    const q = new URLSearchParams();
    q.set('limit', String(Math.min(params.limit ?? 50, 200)));
    if (params.offset != null) q.set('offset', String(params.offset));
    return this.bffRequest(`/bookings/assignment-approvals/pending?${q.toString()}`);
  }

  static async updateBookingSelectedAddons(bookingId, payload) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/selected-addons`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  static async updateBookingSnackbox(bookingId, payload) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/snackbox`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  /** Outbound email send logs (PostgreSQL via BFF). */
  static async getEmailSendLogs(params = {}) {
    const q = new URLSearchParams();
    q.set('limit', String(Math.min(params.limit ?? 200, 200)));
    if (params.offset != null) q.set('offset', String(params.offset));
    if (params.status) q.set('status', params.status);
    if (params.booking_number) q.set('booking_number', String(params.booking_number).trim());
    if (params.booking_id != null && String(params.booking_id).trim()) {
      q.set('booking_id', String(params.booking_id).trim());
    }
    if (params.audience === 'customer' || params.audience === 'vendor') {
      q.set('audience', params.audience);
    }
    if (params.template_key) q.set('template_key', String(params.template_key).trim());
    if (params.customer) q.set('customer', String(params.customer).trim());
    return this.bffRequest(`/admin/email-send-logs?${q.toString()}`);
  }

  /** Outbound SMS send logs (PostgreSQL via BFF). */
  static async getSmsSendLogs(params = {}) {
    const q = new URLSearchParams();
    q.set('limit', String(Math.min(params.limit ?? 200, 200)));
    if (params.offset != null) q.set('offset', String(params.offset));
    if (params.status) q.set('status', params.status);
    if (params.booking_number) q.set('booking_number', String(params.booking_number).trim());
    if (params.booking_id != null && String(params.booking_id).trim()) {
      q.set('booking_id', String(params.booking_id).trim());
    }
    return this.bffRequest(`/admin/sms-send-logs?${q.toString()}`);
  }

  /** Per-booking audit timeline (`booking_activity_logs`) */
  static async getBookingActivityLogs(bookingId, params = {}) {
    const q = new URLSearchParams();
    q.set('limit', String(Math.min(params.limit ?? 200, 500)));
    if (params.offset != null) q.set('offset', String(params.offset));
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/activity-logs?${q.toString()}`);
  }

  /** Driver mobile trip events (`booking_trip_events`) */
  static async getBookingTripEvents(bookingId) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/trip-events`);
  }

  static async uploadBookingTripImage(bookingId, { stage, imageType, file }) {
    const formData = new FormData();
    formData.append('stage', stage);
    formData.append('image_type', imageType);
    formData.append('image', file);
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/trip-upload-image`, {
      method: 'POST',
      body: formData,
    });
  }

  static async updateBookingTripStage(bookingId, payload) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/trip-stage`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async addBookingActivityNote(bookingId, notes, metadata = null) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/activity-logs/note`, {
      method: 'POST',
      body: JSON.stringify(metadata ? { notes, metadata } : { notes }),
    });
  }

  /** Global booking activity (ops / debugging) */
  static async getGlobalBookingActivityLogs(params = {}) {
    const q = new URLSearchParams();
    q.set('limit', String(Math.min(params.limit ?? 100, 500)));
    q.set('offset', String(params.offset ?? 0));
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);
    if (params.event_type) q.set('event_type', params.event_type);
    if (params.event_category) q.set('event_category', params.event_category);
    if (params.booking_id != null && String(params.booking_id).trim()) q.set('booking_id', String(params.booking_id).trim());
    if (params.admin_id != null && String(params.admin_id).trim()) q.set('admin_id', String(params.admin_id).trim());
    if (params.failed_only) q.set('failed_only', '1');
    return this.bffRequest(`/activity-logs?${q.toString()}`);
  }

  /** Update status and optionally link driver / vehicle */
  static async updateBookingStatus(id, payload) {
    return this.bffRequest(`/bookings/${encodeURIComponent(id)}/status`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async getBookingRetryPaymentInfo(bookingId) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/retry-payment-info`);
  }

  static async createBookingRetryPaymentOrder(bookingId, body = {}) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/retry-payment-order`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async markBookingPaymentFailedCod(bookingId, body = {}) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/payment-failed-cod`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async getSupportTicketsStatus() {
    return this.bffRequest('/admin/support-tickets/status');
  }

  static async listSupportTickets(params = {}) {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.limit) q.set('limit', String(params.limit));
    if (params.search) q.set('search', params.search);
    if (params.state) q.set('state', params.state);
    if (params.has_booking) q.set('has_booking', params.has_booking);
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);
    return this.bffRequest(`/admin/support-tickets?${q.toString()}`);
  }

  static async syncSupportTicketsFromZammad(body = {}) {
    return this.bffRequest('/admin/support-tickets/sync', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async linkSupportTicketBooking(zammadTicketId, body) {
    return this.bffRequest(
      `/admin/support-tickets/${encodeURIComponent(zammadTicketId)}/link-booking`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  static async getCrmStats() {
    return this.bffRequest('/admin/crm/stats');
  }

  static async listCrmCallLogs(params = {}) {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.limit) q.set('limit', String(params.limit));
    if (params.search) q.set('search', params.search);
    if (params.direction) q.set('direction', params.direction);
    return this.bffRequest(`/admin/crm/call-logs?${q.toString()}`);
  }

  static async createCrmCallLog(body) {
    return this.bffRequest('/admin/crm/call-logs', { method: 'POST', body: JSON.stringify(body) });
  }

  static async listCrmComplaints(params = {}) {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.limit) q.set('limit', String(params.limit));
    if (params.search) q.set('search', params.search);
    if (params.status) q.set('status', params.status);
    if (params.severity) q.set('severity', params.severity);
    return this.bffRequest(`/admin/crm/complaints?${q.toString()}`);
  }

  static async createCrmComplaint(body) {
    return this.bffRequest('/admin/crm/complaints', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateCrmComplaint(id, body) {
    return this.bffRequest(`/admin/crm/complaints/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async listCrmRefundTickets(params = {}) {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.limit) q.set('limit', String(params.limit));
    if (params.status) q.set('status', params.status);
    if (params.search) q.set('search', params.search);
    return this.bffRequest(`/admin/crm/refund-tickets?${q.toString()}`);
  }

  static async updateCrmRefundTicket(id, body) {
    return this.bffRequest(`/admin/crm/refund-tickets/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async syncCrmRefundTickets() {
    return this.bffRequest('/admin/crm/refund-tickets/sync-cancellations', { method: 'POST', body: '{}' });
  }

  static async listCrmEscalationRules() {
    return this.bffRequest('/admin/crm/escalation-rules');
  }

  static async applyCrmBookingEscalation(bookingRef) {
    return this.bffRequest(`/admin/crm/bookings/${encodeURIComponent(bookingRef)}/apply-escalation`, {
      method: 'POST',
      body: '{}',
    });
  }

  static async getWhatsappSendLogs(params = {}) {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    if (params.status) q.set('status', params.status);
    return this.bffRequest(`/admin/whatsapp-send-logs?${q.toString()}`);
  }

  static async postWhatsappSendLog(body) {
    return this.bffRequest('/admin/whatsapp-send-logs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async getWhatsappSettings() {
    return this.bffRequest('/admin/settings/whatsapp');
  }

  /** Link booking to a vendor (sets bookings.vendor_id) */
  static async assignBookingVendor(bookingId, vendorId) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/vendor`, {
      method: 'PUT',
      body: JSON.stringify({ vendor_id: vendorId }),
    });
  }

  /** Start batched vendor broadcast for a booking (BFF scheduler advances batches) */
  static async startBookingBroadcast(bookingId, body = {}) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/broadcast`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async publishBookingToGuestVendors(bookingId) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/guest-vendor-publish`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async bulkPublishBookingsToGuestVendors(bookingIds) {
    return this.bffRequest('/bookings/guest-vendor-publish/bulk', {
      method: 'POST',
      body: JSON.stringify({ booking_ids: bookingIds }),
    });
  }

  static async getGuestVendorInterests(params = {}) {
    const qs = new URLSearchParams();
    if (params.city) qs.set('city', params.city);
    if (params.booking_id) qs.set('booking_id', params.booking_id);
    if (params.date_from) qs.set('date_from', params.date_from);
    if (params.date_to) qs.set('date_to', params.date_to);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const q = qs.toString();
    return this.bffRequest(`/admin/guest-vendor-interests${q ? `?${q}` : ''}`);
  }

  static async getVendorJoinUsEnquiries(params = {}) {
    const qs = new URLSearchParams();
    if (params.city) qs.set('city', params.city);
    if (params.status) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    if (params.date_from) qs.set('date_from', params.date_from);
    if (params.date_to) qs.set('date_to', params.date_to);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const q = qs.toString();
    return this.bffRequest(`/admin/vendor-join-us-enquiries${q ? `?${q}` : ''}`);
  }

  static async updateVendorJoinUsEnquiry(id, payload = {}) {
    return this.bffRequest(`/admin/vendor-join-us-enquiries/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  static async getBookingGuestVendorBoard(bookingId) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/guest-vendor-board`);
  }

  static async getVendorCabAvailabilityDetail(id) {
    return this.bffRequest(`/admin/vendor-cab-availability/${encodeURIComponent(id)}`);
  }

  static async getVendorCabAvailability(params = {}) {
    const qs = new URLSearchParams();
    if (params.start_city) qs.set('start_city', params.start_city);
    if (params.trip_type) qs.set('trip_type', params.trip_type);
    if (params.phone) qs.set('phone', params.phone);
    if (params.status) qs.set('status', params.status);
    if (params.date_from) qs.set('date_from', params.date_from);
    if (params.date_to) qs.set('date_to', params.date_to);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const q = qs.toString();
    return this.bffRequest(`/admin/vendor-cab-availability${q ? `?${q}` : ''}`);
  }

  static async updateVendorCabAvailabilityStatus(id, status, note) {
    return this.bffRequest(`/admin/vendor-cab-availability/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({
        status,
        ...(note ? { note } : {}),
      }),
    });
  }

  static async getBookingBroadcast(bookingId) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/broadcast`);
  }

  static async sendBookingAction(bookingId, action) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/send`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  }

  static async sendBookingPaymentLink(bookingId) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/send-payment-link`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async getBookingPaymentLinkSends(bookingId) {
    try {
      return await this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/payment-link-sends`);
    } catch (error) {
      const msg = String(error?.message || '');
      // Backward-compat: older BFF builds may not expose /payment-link-sends yet.
      if (!msg.includes('/payment-link-sends')) throw error;
      const q = new URLSearchParams({
        booking_id: String(bookingId),
        template_key: 'booking_payment_link',
        limit: '100',
        offset: '0',
      });
      const legacy = await this.bffRequest(`/admin/email-send-logs?${q.toString()}`);
      return {
        success: true,
        data: {
          booking_id: bookingId,
          booking_number: null,
          email_sends: legacy?.data?.logs || legacy?.logs || [],
          payment_links: [],
        },
      };
    }
  }

  static async fetchBookingInvoiceHtml(bookingId, { print = false } = {}) {
    const qs = print ? '?print=1' : '';
    const url = `${getBffBaseUrl()}/bookings/${encodeURIComponent(bookingId)}/invoice${qs}`;
    const res = await fetch(url, { headers: this.adminBearerHeaders() });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Failed to load invoice');
    }
    return res.text();
  }

  /** @deprecated Prefer in-page InvoicePreviewModal — pop-ups are blocked after async fetch. */
  static async printBookingInvoice(bookingId) {
    const html = await this.fetchBookingInvoiceHtml(bookingId, { print: true });
    const win = window.open('about:blank', '_blank', 'noopener,noreferrer');
    if (!win) throw new Error('Pop-up blocked. Use View invoice instead.');
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
  }

  /** @deprecated Prefer in-page InvoicePreviewModal — pop-ups are blocked after async fetch. */
  static async viewBookingInvoice(bookingId) {
    const html = await this.fetchBookingInvoiceHtml(bookingId);
    const win = window.open('about:blank', '_blank', 'noopener,noreferrer');
    if (!win) throw new Error('Pop-up blocked. Use View invoice instead.');
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
  }

  static async emailBookingInvoice(bookingId) {
    return this.sendBookingAction(bookingId, 'SEND_INVOICE_TO_CUSTOMER');
  }

  static async generateBookingInvoice(bookingId) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/generate-invoice`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async generateManualBill(bookingId, body) {
    return this.bffRequest(`/bookings/${encodeURIComponent(bookingId)}/generate-manual-bill`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  /** Booking status master CRUD (`booking_statuses`). */
  static async getBookingStatuses() {
    return this.bffRequest('/booking-statuses');
  }

  static async createBookingStatus(input) {
    return this.bffRequest('/booking-statuses', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateBookingStatusMaster(id, input) {
    return this.bffRequest(`/booking-statuses/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteBookingStatusMaster(id) {
    return this.bffRequest(`/booking-statuses/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async listStatuses(params = {}) {
    const q = new URLSearchParams();
    if (params.entity_type) q.set('entity_type', String(params.entity_type).trim().toLowerCase());
    if (params.active != null) q.set('active', String(!!params.active));
    if (params.visible_for_user != null) q.set('visible_for_user', String(!!params.visible_for_user));
    if (params.include_all != null) q.set('include_all', String(!!params.include_all));
    const qs = q.toString();
    return this.bffRequest(`/statuses${qs ? `?${qs}` : ''}`);
  }

  static async createStatus(input) {
    return this.bffRequest('/statuses', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateStatus(id, input) {
    return this.bffRequest(`/statuses/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async reorderStatuses(entityType, orderedIds) {
    const payload = {
      entity_type: entityType,
      ordered_ids: orderedIds.map((id) => parseInt(id, 10)).filter((id) => Number.isFinite(id) && id > 0),
    };
    return this.bffRequest('/statuses/reorder', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async deleteStatus(id) {
    return this.bffRequest(`/statuses/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async listStatusTransitions(params = {}) {
    const q = new URLSearchParams();
    if (params.entity_type) q.set('entity_type', String(params.entity_type).trim().toLowerCase());
    const qs = q.toString();
    return this.bffRequest(`/statuses/transitions${qs ? `?${qs}` : ''}`);
  }

  static async createStatusTransition(input) {
    return this.bffRequest('/statuses/transitions', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async deleteStatusTransition(id) {
    return this.bffRequest(`/statuses/transitions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async listStatusAutoRules(params = {}) {
    const q = new URLSearchParams();
    if (params.entity_type) q.set('entity_type', String(params.entity_type).trim().toLowerCase());
    const qs = q.toString();
    return this.bffRequest(`/statuses/auto-rules${qs ? `?${qs}` : ''}`);
  }

  static async createStatusAutoRule(input) {
    return this.bffRequest('/statuses/auto-rules', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateStatusAutoRule(id, input) {
    return this.bffRequest(`/statuses/auto-rules/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteStatusAutoRule(id) {
    return this.bffRequest(`/statuses/auto-rules/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** Admin: vendor broadcast / bid rows (joined offers + bids) */
  static async getBroadcastLogs(params = {}) {
    const q = new URLSearchParams();
    if (params.city?.trim()) q.set('city', params.city.trim());
    if (params.booking_id != null && String(params.booking_id).trim()) q.set('booking_id', String(params.booking_id).trim());
    if (params.vendor_id != null && String(params.vendor_id).trim()) q.set('vendor_id', String(params.vendor_id).trim());
    if (params.status != null && String(params.status).trim()) q.set('status', String(params.status).trim());
    if (params.date_from != null && String(params.date_from).trim()) q.set('date_from', String(params.date_from).trim());
    if (params.date_to != null && String(params.date_to).trim()) q.set('date_to', String(params.date_to).trim());
    q.set('limit', String(params.limit ?? 50));
    q.set('offset', String(params.offset ?? 0));
    const qs = q.toString();
    return this.bffRequest(`/admin/broadcast-logs${qs ? `?${qs}` : ''}`);
  }

  /** Admin: vendor panel notification banners */
  static async listVendorNotifications(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && String(v).trim() !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/notifications${qs ? `?${qs}` : ''}`);
  }

  static async getVendorNotification(id) {
    return this.bffRequest(`/admin/notifications/${encodeURIComponent(id)}`);
  }

  static async createVendorNotification(body) {
    return this.bffRequest('/admin/notifications', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateVendorNotification(id, body) {
    return this.bffRequest(`/admin/notifications/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async deleteVendorNotification(id) {
    return this.bffRequest(`/admin/notifications/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async listVendorPenalties(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && String(v).trim() !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/vendor-penalties${qs ? `?${qs}` : ''}`);
  }

  static async getVendorPenalty(id) {
    return this.bffRequest(`/admin/vendor-penalties/${encodeURIComponent(id)}`);
  }

  static async createVendorPenalty(body) {
    return this.bffRequest('/admin/vendor-penalties', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateVendorPenalty(id, body) {
    return this.bffRequest(`/admin/vendor-penalties/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async deleteVendorPenalty(id) {
    return this.bffRequest(`/admin/vendor-penalties/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async publishVendorNotification(id, body = {}) {
    return this.bffRequest(`/admin/notifications/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async archiveVendorNotification(id) {
    return this.bffRequest(`/admin/notifications/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
      body: '{}',
    });
  }

  static async duplicateVendorNotification(id) {
    return this.bffRequest(`/admin/notifications/${encodeURIComponent(id)}/duplicate`, {
      method: 'POST',
      body: '{}',
    });
  }

  static async previewVendorNotification(id, vendorId) {
    return this.bffRequest(
      `/admin/notifications/${encodeURIComponent(id)}/preview/${encodeURIComponent(vendorId)}`,
    );
  }

  static async acceptBroadcastOffer(offerId, vendorId) {
    return this.bffRequest(`/broadcast-offers/${encodeURIComponent(offerId)}/accept`, {
      method: 'POST',
      body: JSON.stringify({ vendor_id: vendorId }),
    });
  }

  static async getDashboard() {
    return this.bffRequest('/dashboard');
  }

  static async getDashboardTrends(days = 7) {
    const d = Math.min(Math.max(parseInt(String(days), 10) || 7, 1), 365);
    return this.bffRequest(`/dashboard/trends?days=${d}`);
  }

  static async getCabPackagesAdmin() {
    return this.bffRequest('/admin/cab-packages');
  }

  static async createCabPackage(body) {
    return this.bffRequest('/admin/cab-packages', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateCabPackage(id, body) {
    return this.bffRequest(`/admin/cab-packages/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  static async importCabPackagesCatalog() {
    return this.bffRequest('/admin/cab-packages/import-catalog', { method: 'POST', body: '{}' });
  }

  static async importCabPackagesHotelSamples() {
    return this.bffRequest('/admin/cab-packages/import-hotel-samples', { method: 'POST', body: '{}' });
  }

  static async importCabPackagesCabOnlyExtended() {
    return this.bffRequest('/admin/cab-packages/import-cab-only-extended', { method: 'POST', body: '{}' });
  }

  static async listPilgrimageSitesAdmin() {
    return this.bffRequest('/admin/pilgrimage-sites');
  }

  static async getPilgrimageSiteAdmin(id) {
    return this.bffRequest(`/admin/pilgrimage-sites/${encodeURIComponent(id)}`);
  }

  static async createPilgrimageSite(body) {
    return this.bffRequest('/admin/pilgrimage-sites', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updatePilgrimageSite(id, body) {
    return this.bffRequest(`/admin/pilgrimage-sites/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async deletePilgrimageSite(id) {
    return this.bffRequest(`/admin/pilgrimage-sites/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async getCabPackageEnquiries(params = {}) {
    const q = new URLSearchParams();
    const keys = [
      'status',
      'search',
      'limit',
      'offset',
      'date_from',
      'date_to',
      'travel_date_from',
      'travel_date_to',
      'from_city',
      'to_city',
      'package_slug',
    ];
    for (const key of keys) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/admin/cab-package-enquiries${qs ? `?${qs}` : ''}`);
  }

  static async updateCabPackageEnquiry(id, body) {
    return this.bffRequest(`/admin/cab-package-enquiries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async getCorporateCmsHub() {
    return this.bffRequest('/corporate-cms/admin/hub');
  }

  static async updateCorporateCmsHub(body) {
    return this.bffRequest('/corporate-cms/admin/hub', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async getCorporateCmsCities(params = {}) {
    const q = new URLSearchParams();
    for (const key of ['status', 'search', 'limit', 'offset']) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/corporate-cms/admin/cities${qs ? `?${qs}` : ''}`);
  }

  static async getCityMoneyPages(params = {}) {
    const q = new URLSearchParams();
    for (const key of ['status', 'search', 'limit', 'offset']) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/city-money-pages/admin${qs ? `?${qs}` : ''}`);
  }

  static async createCityMoneyPage(body) {
    return this.bffRequest('/city-money-pages/admin', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateCityMoneyPage(id, body) {
    return this.bffRequest(`/city-money-pages/admin/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async validateCityMoneyPage(id) {
    return this.bffRequest(`/city-money-pages/admin/${id}/validate`, { method: 'POST' });
  }

  static async publishCityMoneyPage(id) {
    return this.bffRequest(`/city-money-pages/admin/${id}/publish`, { method: 'POST' });
  }

  static async unpublishCityMoneyPage(id) {
    return this.bffRequest(`/city-money-pages/admin/${id}/unpublish`, { method: 'POST' });
  }

  static async getSeoScoreDashboard(params = {}) {
    const q = new URLSearchParams();
    if (params.persist) q.set('persist', '1');
    const qs = q.toString();
    return this.bffRequest(`/seo/admin/score-dashboard${qs ? `?${qs}` : ''}`);
  }

  static async refreshSeoScoreDashboard() {
    return this.bffRequest('/seo/admin/score-dashboard/refresh', { method: 'POST' });
  }

  static async getLocalityLandingsAdmin(params = {}) {
    const q = new URLSearchParams();
    for (const key of ['city', 'status', 'search', 'limit', 'offset']) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/locality-landings/admin${qs ? `?${qs}` : ''}`);
  }

  static async createLocalityLanding(body) {
    return this.bffRequest('/locality-landings/admin', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateLocalityLanding(id, body) {
    return this.bffRequest(`/locality-landings/admin/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async validateLocalityLanding(id) {
    return this.bffRequest(`/locality-landings/admin/${encodeURIComponent(id)}/validate`, {
      method: 'POST',
    });
  }

  static async deleteLocalityLanding(id) {
    return this.bffRequest(`/locality-landings/admin/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async createCorporateCmsCity(body) {
    return this.bffRequest('/corporate-cms/admin/cities', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateCorporateCmsCity(id, body) {
    return this.bffRequest(`/corporate-cms/admin/cities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async getCorporateCmsServices(params = {}) {
    const q = new URLSearchParams();
    for (const key of ['status', 'search', 'limit', 'offset']) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/corporate-cms/admin/services${qs ? `?${qs}` : ''}`);
  }

  static async createCorporateCmsService(body) {
    return this.bffRequest('/corporate-cms/admin/services', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateCorporateCmsService(id, body) {
    return this.bffRequest(`/corporate-cms/admin/services/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async getCorporateCmsPackages(params = {}) {
    const q = new URLSearchParams();
    for (const key of ['status', 'city_id', 'search', 'limit', 'offset']) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/corporate-cms/admin/packages${qs ? `?${qs}` : ''}`);
  }

  static async createCorporateCmsPackage(body) {
    return this.bffRequest('/corporate-cms/admin/packages', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateCorporateCmsPackage(id, body) {
    return this.bffRequest(`/corporate-cms/admin/packages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async getCorporateCmsEnquiries(params = {}) {
    const q = new URLSearchParams();
    for (const key of ['status', 'search', 'city', 'limit', 'offset', 'date_from', 'date_to']) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/corporate-cms/admin/enquiries${qs ? `?${qs}` : ''}`);
  }

  static async updateCorporateCmsEnquiry(id, body) {
    return this.bffRequest(`/corporate-cms/admin/enquiries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async getCorporateCmsLocalities(params = {}) {
    const q = new URLSearchParams();
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.offset != null) q.set('offset', String(params.offset));
    if (params.city_id != null && params.city_id !== '') q.set('city_id', String(params.city_id));
    const qs = q.toString();
    return this.bffRequest(`/corporate-cms/admin/localities${qs ? `?${qs}` : ''}`);
  }

  static async createCorporateCmsLocality(body) {
    return this.bffRequest('/corporate-cms/admin/localities', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateCorporateCmsLocality(id, body) {
    return this.bffRequest(`/corporate-cms/admin/localities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  // ---- Ashtavinayak CMS admin (`/ashtavinayak-cms/admin/*`) ----
  // Contract mirrors BFF ashtavinayakCmsRoutes.js
  static ashtavinayakAdminQs(params = {}, keys = ['status', 'search', 'limit', 'offset']) {
    const q = new URLSearchParams();
    for (const key of keys) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    return q.toString();
  }

  static async getAshtavinayakTours(params = {}) {
    const qs = this.ashtavinayakAdminQs(params, ['status', 'origin', 'limit', 'offset']);
    return this.bffRequest(`/ashtavinayak-cms/admin/tours${qs ? `?${qs}` : ''}`);
  }

  static async getAshtavinayakTour(id) {
    return this.bffRequest(`/ashtavinayak-cms/admin/tours/${encodeURIComponent(id)}`);
  }

  static async createAshtavinayakTour(body) {
    return this.bffRequest('/ashtavinayak-cms/admin/tours', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateAshtavinayakTour(id, body) {
    return this.bffRequest(`/ashtavinayak-cms/admin/tours/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async publishAshtavinayakTour(id) {
    return this.bffRequest(`/ashtavinayak-cms/admin/tours/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
    });
  }

  static async unpublishAshtavinayakTour(id) {
    return this.bffRequest(`/ashtavinayak-cms/admin/tours/${encodeURIComponent(id)}/unpublish`, {
      method: 'POST',
    });
  }

  static async getAshtavinayakTemples(params = {}) {
    const qs = this.ashtavinayakAdminQs(params);
    return this.bffRequest(`/ashtavinayak-cms/admin/temples${qs ? `?${qs}` : ''}`);
  }

  static async createAshtavinayakTemple(body) {
    return this.bffRequest('/ashtavinayak-cms/admin/temples', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateAshtavinayakTemple(id, body) {
    return this.bffRequest(`/ashtavinayak-cms/admin/temples/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async deleteAshtavinayakTemple(id) {
    return this.bffRequest(`/ashtavinayak-cms/admin/temples/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async createAshtavinayakItinerary(body) {
    return this.bffRequest('/ashtavinayak-cms/admin/itineraries', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateAshtavinayakItinerary(id, body) {
    return this.bffRequest(`/ashtavinayak-cms/admin/itineraries/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async deleteAshtavinayakItinerary(id) {
    return this.bffRequest(`/ashtavinayak-cms/admin/itineraries/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async createAshtavinayakFaq(body) {
    return this.bffRequest('/ashtavinayak-cms/admin/faqs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateAshtavinayakFaq(id, body) {
    return this.bffRequest(`/ashtavinayak-cms/admin/faqs/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async deleteAshtavinayakFaq(id) {
    return this.bffRequest(`/ashtavinayak-cms/admin/faqs/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async createAshtavinayakPickup(body) {
    return this.bffRequest('/ashtavinayak-cms/admin/pickups', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateAshtavinayakPickup(id, body) {
    return this.bffRequest(`/ashtavinayak-cms/admin/pickups/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async deleteAshtavinayakPickup(id) {
    return this.bffRequest(`/ashtavinayak-cms/admin/pickups/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async createAshtavinayakLink(body) {
    return this.bffRequest('/ashtavinayak-cms/admin/links', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateAshtavinayakLink(id, body) {
    return this.bffRequest(`/ashtavinayak-cms/admin/links/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async deleteAshtavinayakLink(id) {
    return this.bffRequest(`/ashtavinayak-cms/admin/links/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async generateAshtavinayakAiDraft(tourId, body) {
    return this.bffRequest(
      `/ashtavinayak-cms/admin/tours/${encodeURIComponent(tourId)}/ai/generate`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  static async listAshtavinayakAiDrafts(tourId, params = {}) {
    const qs = this.ashtavinayakAdminQs(params, ['status']);
    return this.bffRequest(
      `/ashtavinayak-cms/admin/tours/${encodeURIComponent(tourId)}/ai/drafts${qs ? `?${qs}` : ''}`,
    );
  }

  static async approveAshtavinayakAiDraft(draftId) {
    return this.bffRequest(
      `/ashtavinayak-cms/admin/ai-drafts/${encodeURIComponent(draftId)}/approve`,
      { method: 'POST' },
    );
  }

  static async rejectAshtavinayakAiDraft(draftId, body = {}) {
    return this.bffRequest(
      `/ashtavinayak-cms/admin/ai-drafts/${encodeURIComponent(draftId)}/reject`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  static async applyAshtavinayakAiDraft(draftId) {
    return this.bffRequest(
      `/ashtavinayak-cms/admin/ai-drafts/${encodeURIComponent(draftId)}/apply`,
      { method: 'POST' },
    );
  }

  static async getCabPackageUserActivityDashboard(params = {}) {
    const q = new URLSearchParams();
    const keys = [
      'search',
      'date_from',
      'date_to',
      'event_type',
      'page_type',
      'package_slug',
      'from_city',
      'to_city',
      'device_type',
      'session_id',
    ];
    for (const key of keys) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/admin/cab-package-user-activity/dashboard${qs ? `?${qs}` : ''}`);
  }

  static async listCabPackageUserActivity(params = {}) {
    const q = new URLSearchParams();
    const keys = [
      'search',
      'limit',
      'offset',
      'date_from',
      'date_to',
      'event_type',
      'page_type',
      'package_slug',
      'from_city',
      'to_city',
      'device_type',
      'session_id',
    ];
    for (const key of keys) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/admin/cab-package-user-activity${qs ? `?${qs}` : ''}`);
  }

  static _pageUserActivityQuery(params = {}) {
    const q = new URLSearchParams();
    const keys = [
      'search',
      'limit',
      'offset',
      'date_from',
      'date_to',
      'event_type',
      'page_type',
      'package_slug',
      'entity_slug',
      'from_city',
      'to_city',
      'device_type',
      'session_id',
    ];
    for (const key of keys) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    return q.toString();
  }

  static async getRoutePageUserActivityDashboard(params = {}) {
    const qs = this._pageUserActivityQuery(params);
    return this.bffRequest(`/admin/route-page-user-activity/dashboard${qs ? `?${qs}` : ''}`);
  }

  static async listRoutePageUserActivity(params = {}) {
    const qs = this._pageUserActivityQuery(params);
    return this.bffRequest(`/admin/route-page-user-activity${qs ? `?${qs}` : ''}`);
  }

  static async getVehicleLandingUserActivityDashboard(params = {}) {
    const qs = this._pageUserActivityQuery(params);
    return this.bffRequest(`/admin/vehicle-landing-user-activity/dashboard${qs ? `?${qs}` : ''}`);
  }

  static async listVehicleLandingUserActivity(params = {}) {
    const qs = this._pageUserActivityQuery(params);
    return this.bffRequest(`/admin/vehicle-landing-user-activity${qs ? `?${qs}` : ''}`);
  }

  static async getContactMessages(params = {}) {
    const q = new URLSearchParams();
    const keys = [
      'status',
      'search',
      'limit',
      'offset',
      'date_from',
      'date_to',
      'travel_date_from',
      'travel_date_to',
      'service_type',
      'pickup_city',
      'destination',
    ];
    for (const key of keys) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/admin/contact-messages${qs ? `?${qs}` : ''}`);
  }

  static async getQuoteEnquiries(params = {}) {
    const q = new URLSearchParams();
    const keys = [
      'status',
      'search',
      'limit',
      'offset',
      'date_from',
      'date_to',
      'from_city',
      'to_city',
      'trip_type',
    ];
    for (const key of keys) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/admin/quote-enquiries${qs ? `?${qs}` : ''}`);
  }

  static async getQuoteEnquiry(id) {
    return this.bffRequest(`/admin/quote-enquiries/${encodeURIComponent(id)}`);
  }

  static async updateQuoteEnquiry(id, body) {
    return this.bffRequest(`/admin/quote-enquiries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async updateContactMessage(id, body) {
    return this.bffRequest(`/admin/contact-messages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async getNewsletterSubscribers(params = {}) {
    const q = new URLSearchParams();
    const keys = ['status', 'search', 'source', 'limit', 'offset', 'date_from', 'date_to'];
    for (const key of keys) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/admin/newsletter-subscribers${qs ? `?${qs}` : ''}`);
  }

  static async updateNewsletterSubscriber(id, body) {
    return this.bffRequest(`/admin/newsletter-subscribers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async getNewsletterCampaigns(params = {}) {
    const q = new URLSearchParams();
    for (const key of ['status', 'search', 'limit', 'offset']) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/admin/newsletter-campaigns${qs ? `?${qs}` : ''}`);
  }

  static async getNewsletterCampaign(id) {
    return this.bffRequest(`/admin/newsletter-campaigns/${id}`);
  }

  static async getNewsletterCampaignDefaults() {
    return this.bffRequest('/admin/newsletter-campaigns/defaults');
  }

  static async createNewsletterCampaign(body) {
    return this.bffRequest('/admin/newsletter-campaigns', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateNewsletterCampaign(id, body) {
    return this.bffRequest(`/admin/newsletter-campaigns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async sendNewsletterCampaign(id) {
    return this.bffRequest(`/admin/newsletter-campaigns/${id}/send`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async sendNewsletterWeeklyNow(body = {}) {
    return this.bffRequest('/admin/newsletter-campaigns/send-weekly-now', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async cancelNewsletterCampaign(id) {
    return this.bffRequest(`/admin/newsletter-campaigns/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async uploadCabPackageImage(file, slug) {
    const formData = new FormData();
    formData.append('image', file);
    if (slug) formData.append('slug', slug);
    return this.bffRequest('/admin/cab-packages/upload-image', { method: 'POST', body: formData });
  }

  static async uploadCabPackagePdf(file, slug) {
    const formData = new FormData();
    formData.append('pdf', file);
    if (slug) formData.append('slug', slug);
    return this.bffRequest('/admin/cab-packages/upload-pdf', { method: 'POST', body: formData });
  }

  static async getNearbyPlacesMeta() {
    return this.bffRequest('/admin/nearby-places/meta');
  }

  static async getNearbyPlacesAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/nearby-places${qs ? `?${qs}` : ''}`);
  }

  static async getNearbyPlaceAdmin(id) {
    return this.bffRequest(`/admin/nearby-places/${id}`);
  }

  static async createNearbyPlace(body) {
    return this.bffRequest('/admin/nearby-places', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateNearbyPlace(id, body) {
    return this.bffRequest(`/admin/nearby-places/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  static async deleteNearbyPlaces(ids) {
    return this.bffRequest('/admin/nearby-places', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    });
  }

  static async bulkNearbyPlacesStatus(ids, isActive) {
    return this.bffRequest('/admin/nearby-places/bulk-status', {
      method: 'POST',
      body: JSON.stringify({ ids, is_active: isActive }),
    });
  }

  static async bulkNearbyPlacesTripTypes({ ids, tripTypes, applyToFilter = false, filters = {} }) {
    return this.bffRequest('/admin/nearby-places/bulk-status', {
      method: 'POST',
      body: JSON.stringify({
        action: 'trip_types',
        ids,
        trip_types: tripTypes,
        apply_to_filter: applyToFilter,
        filters,
      }),
    });
  }

  static async suggestNearbyPlaceUrlKey(placeName) {
    return this.bffRequest('/admin/nearby-places/suggest-url-key', {
      method: 'POST',
      body: JSON.stringify({ place_name: placeName }),
    });
  }

  static async uploadNearbyPlaceImage(file, urlKey) {
    const formData = new FormData();
    formData.append('image', file);
    if (urlKey) formData.append('url_key', urlKey);
    return this.bffRequest('/admin/nearby-places/upload-image', { method: 'POST', body: formData });
  }

  static async exportNearbyPlacesCsv(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    const url = `${getBffBaseUrl()}/admin/nearby-places/export/csv${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'text/csv', ...this.adminBearerHeaders() },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Export failed');
    }
    return res.blob();
  }

  static async getAdminAccessLogs(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/access-logs${qs ? `?${qs}` : ''}`);
  }

  static async getAdminAccessLog(id) {
    return this.bffRequest(`/admin/access-logs/${encodeURIComponent(id)}`);
  }

  static async getCustomerActivities(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/customer-activities${qs ? `?${qs}` : ''}`);
  }

  static async getBusinessPresenceMeta() {
    return this.bffRequest('/admin/business-presence/meta');
  }

  static async getBusinessPresenceDashboard(params = {}) {
    const q = new URLSearchParams();
    if (params.location_id) q.set('location_id', String(params.location_id));
    const qs = q.toString();
    return this.bffRequest(`/admin/business-presence/dashboard${qs ? `?${qs}` : ''}`);
  }

  static async getBusinessPresenceLocations(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/business-presence/locations${qs ? `?${qs}` : ''}`);
  }

  static async createBusinessPresenceLocation(body) {
    return this.bffRequest('/admin/business-presence/locations', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async importBusinessPresenceGoogleLocation(body) {
    return this.bffRequest('/admin/business-presence/locations/import-google', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateBusinessPresenceLocation(id, body) {
    return this.bffRequest(`/admin/business-presence/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async getBusinessPresencePosts(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/business-presence/posts${qs ? `?${qs}` : ''}`);
  }

  static async generateBusinessPresencePost(body) {
    return this.bffRequest('/admin/business-presence/posts/generate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async getBusinessPresenceReviews(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/business-presence/reviews${qs ? `?${qs}` : ''}`);
  }

  static async generateBusinessPresenceReviewReply(id, body = {}) {
    return this.bffRequest(`/admin/business-presence/reviews/${id}/generate-reply`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async getBusinessPresenceSettings(params = {}) {
    const q = new URLSearchParams();
    if (params.location_id) q.set('location_id', String(params.location_id));
    const qs = q.toString();
    return this.bffRequest(`/admin/business-presence/settings${qs ? `?${qs}` : ''}`);
  }

  static async saveBusinessPresenceSettings(body) {
    return this.bffRequest('/admin/business-presence/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async publishBusinessPresencePost(id) {
    return this.bffRequest(`/admin/business-presence/posts/${id}/publish`, { method: 'POST' });
  }

  static async scheduleBusinessPresencePost(id, scheduledAt) {
    return this.bffRequest(`/admin/business-presence/posts/${id}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ scheduled_at: scheduledAt }),
    });
  }

  static async getBusinessPresenceSocialAccounts(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/business-presence/social-accounts${qs ? `?${qs}` : ''}`);
  }

  static async startBusinessPresenceOAuth(provider, params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(
      `/admin/business-presence/oauth/${encodeURIComponent(provider)}/authorize${qs ? `?${qs}` : ''}`,
    );
  }

  static async completeBusinessPresenceOAuth(provider, body) {
    return this.bffRequest(`/admin/business-presence/oauth/${encodeURIComponent(provider)}/callback`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async disconnectBusinessPresenceSocialAccount(id) {
    return this.bffRequest(`/admin/business-presence/social-accounts/${id}`, { method: 'DELETE' });
  }

  static async syncBusinessPresenceReviews(body = {}) {
    return this.bffRequest('/admin/business-presence/reviews/sync', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async getBusinessPresenceSeoPages(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/business-presence/seo/pages${qs ? `?${qs}` : ''}`);
  }

  static async generateBusinessPresenceSeoPage(body) {
    return this.bffRequest('/admin/business-presence/seo/pages/generate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateBusinessPresenceSeoPageStatus(id, status) {
    return this.bffRequest(`/admin/business-presence/seo/pages/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  static async getFinanceVendorSettlement() {
    return this.bffRequest('/finance/vendor-settlement');
  }

  static async getFinanceReport(reportKey, { days = 90 } = {}) {
    const d = Math.min(Math.max(parseInt(days, 10) || 90, 7), 365);
    return this.bffRequest(`/finance/reports/${encodeURIComponent(reportKey)}?days=${d}`);
  }

  static async bulkUpdateGstSubmission({ bookingIds, gstSubmissionStatus, gstSubmissionRemarks }) {
    return this.bffRequest('/finance/gst-invoices/bulk-submission', {
      method: 'POST',
      body: JSON.stringify({
        booking_ids: bookingIds,
        gst_submission_status: gstSubmissionStatus,
        gst_submission_remarks: gstSubmissionRemarks,
      }),
    });
  }

  static async getFinanceDashboard({ days = 30 } = {}) {
    const d = Math.min(Math.max(parseInt(days, 10) || 30, 7), 365);
    return this.bffRequest(`/finance/dashboard?days=${d}`);
  }

  static async getFinanceVehicleLedger({ days = 90 } = {}) {
    const d = Math.min(Math.max(parseInt(days, 10) || 90, 7), 365);
    return this.bffRequest(`/finance/vehicle-ledger?days=${d}`);
  }

  static async getFinanceVendorFinance({ days = 90 } = {}) {
    const d = Math.min(Math.max(parseInt(days, 10) || 90, 7), 365);
    return this.bffRequest(`/finance/vendor-finance?days=${d}`);
  }

  static async getFinanceExpenses({ days = 90, category } = {}) {
    const d = Math.min(Math.max(parseInt(days, 10) || 90, 7), 365);
    let url = `/finance/expenses?days=${d}`;
    if (category) url += `&category=${encodeURIComponent(category)}`;
    return this.bffRequest(url);
  }

  static async createFinanceExpense(payload) {
    return this.bffRequest('/finance/expenses', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async getFinanceDriverSettlementEntries({ month } = {}) {
    let url = '/finance/driver-settlement-entries';
    if (month) url += `?month=${encodeURIComponent(month)}`;
    return this.bffRequest(url);
  }

  static async createFinanceDriverSettlementEntry(payload) {
    return this.bffRequest('/finance/driver-settlement-entries', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async createFinanceVendorLedgerEntry(payload) {
    return this.bffRequest('/finance/vendor-ledger', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async createVendorPendingPaymentLink(payload) {
    return this.bffRequest('/finance/vendor-pending-payment-links', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async getVendorPenaltyMatrix() {
    return this.bffRequest('/admin/vendor-penalties/matrix');
  }

  static async getVendorPenaltyEvents(query = '') {
    const q = query ? (query.startsWith('?') ? query : `?${query}`) : '';
    return this.bffRequest(`/admin/vendor-penalties/events${q}`);
  }

  static async getVendorPenaltyState(vendorId) {
    return this.bffRequest(`/admin/vendor-penalties/vendor/${vendorId}`);
  }

  static async uploadVendorPenaltyImage(file) {
    const formData = new FormData();
    formData.append('image', file);
    return this.bffRequest('/admin/vendor-penalties/upload-image', {
      method: 'POST',
      body: formData,
    });
  }

  static async applyVendorPenalty(payload) {
    return this.bffRequest('/admin/vendor-penalties/apply', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async waiveVendorPenalty(id, payload = {}) {
    return this.bffRequest(`/admin/vendor-penalties/${id}/waive`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async runVendorPenaltyAutoSweep(config = {}) {
    return this.bffRequest('/admin/vendor-penalties/run-auto', {
      method: 'POST',
      body: JSON.stringify({ config }),
    });
  }

  static async syncVendorCommissionPaymentLinks(vendorId = null) {
    const q = vendorId != null ? `?vendor_id=${encodeURIComponent(vendorId)}` : '';
    return this.bffRequest(`/finance/vendor-pending-payment-links/sync-commission${q}`, {
      method: 'POST',
    });
  }

  static async parseFastagCsv({ csv, filename }) {
    return this.bffRequest('/finance/fastag/parse', {
      method: 'POST',
      body: JSON.stringify({ csv, filename }),
    });
  }

  static async importFastagRows({ rows, filename }) {
    return this.bffRequest('/finance/fastag/import', {
      method: 'POST',
      body: JSON.stringify({ rows, filename }),
    });
  }

  static async migrateFinanceLocalExpenses(entries) {
    return this.bffRequest('/finance/expenses/migrate-local', {
      method: 'POST',
      body: JSON.stringify({ entries }),
    });
  }

  static async getFinanceZohoExport(exportType, { days = 90 } = {}) {
    const d = Math.min(Math.max(parseInt(days, 10) || 90, 7), 365);
    return this.bffRequest(`/finance/zoho-export/${encodeURIComponent(exportType)}?days=${d}`);
  }

  static async getDashboardPopularRoutes(limit = 5) {
    return this.bffRequest(`/dashboard/popular-routes?limit=${limit}`);
  }

  static async getFleetExpiringDocuments(days = 30) {
    return this.bffRequest(`/fleet/expiring-documents?days=${days}`);
  }

  static async getVendors(params = {}) {
    const q = new URLSearchParams();
    q.set('limit', String(Math.min(params.limit || 500, 1000)));
    q.set('offset', String(params.offset || 0));
    if (params.status) q.set('status', params.status);
    if (params.city) q.set('city', params.city);
    return this.bffRequest(`/vendors?${q.toString()}`);
  }

  static async getVendor(id) {
    return this.bffRequest(`/vendors/${encodeURIComponent(id)}`);
  }

  static async listVendorLocationMappings(vendorId) {
    return this.bffRequest(`/admin/vendors/${encodeURIComponent(vendorId)}/location-mappings`);
  }

  static async createVendorLocationMapping(vendorId, body) {
    return this.bffRequest(`/admin/vendors/${encodeURIComponent(vendorId)}/location-mappings`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async syncVendorLocationMappings(vendorId, body = {}) {
    return this.bffRequest(`/admin/vendors/${encodeURIComponent(vendorId)}/location-mappings/sync`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async deleteVendorLocationMapping(vendorId, mappingId) {
    return this.bffRequest(
      `/admin/vendors/${encodeURIComponent(vendorId)}/location-mappings/${encodeURIComponent(mappingId)}`,
      { method: 'DELETE' },
    );
  }

  /** Per-vendor activity log (`vendor_activities`) for the "Vendor log" tab. */
  static async getVendorActivities(id, params = {}) {
    const q = new URLSearchParams();
    q.set('limit', String(Math.min(params.limit ?? 100, 200)));
    if (params.page != null) q.set('page', String(params.page));
    if (params.activity_type) q.set('activity_type', params.activity_type);
    return this.bffRequest(`/admin/vendors/${encodeURIComponent(id)}/activities?${q.toString()}`);
  }

  static async createVendor(input) {
    return this.bffRequest('/vendors', { method: 'POST', body: JSON.stringify(input) });
  }

  static async updateVendor(id, input) {
    return this.bffRequest(`/vendors/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  }

  static async deleteVendor(id) {
    return this.bffRequest(`/vendors/${id}`, { method: 'DELETE' });
  }

  static async getPanIndiaVendorsAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/pan-india-vendors${qs ? `?${qs}` : ''}`);
  }

  static async getPanIndiaVendorsMeta() {
    return this.bffRequest('/admin/pan-india-vendors/meta');
  }

  static async getPanIndiaVendor(id) {
    return this.bffRequest(`/admin/pan-india-vendors/${encodeURIComponent(id)}`);
  }

  static async updatePanIndiaVendor(id, body) {
    return this.bffRequest(`/admin/pan-india-vendors/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async exportPanIndiaVendorsCsv(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    const url = `${getBffBaseUrl()}/admin/pan-india-vendors/export/csv${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'text/csv', ...this.adminBearerHeaders() },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Export failed');
    }
    return res.blob();
  }

  static async getDmcCabProspectsAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/dmc/cab-prospects${qs ? `?${qs}` : ''}`);
  }

  static async getDmcCabProspectsMeta() {
    return this.bffRequest('/admin/dmc/cab-prospects/meta');
  }

  static async createDmcCabProspect(body) {
    return this.bffRequest('/admin/dmc/cab-prospects', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateDmcCabProspect(id, body) {
    return this.bffRequest(`/admin/dmc/cab-prospects/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async deleteDmcCabProspect(id) {
    return this.bffRequest(`/admin/dmc/cab-prospects/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async importDmcCabProspectsXlsx(file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.bffRequest('/admin/dmc/cab-prospects/import/xlsx', { method: 'POST', body: formData });
  }

  static async exportDmcCabProspectsCsv(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    const url = `${getBffBaseUrl()}/admin/dmc/cab-prospects/export/csv${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'text/csv', ...this.adminBearerHeaders() },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Export failed');
    }
    return res.blob();
  }

  static async getDmcPackageProspectsAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/dmc/package-prospects${qs ? `?${qs}` : ''}`);
  }

  static async getDmcPackageProspectsMeta() {
    return this.bffRequest('/admin/dmc/package-prospects/meta');
  }

  static async createDmcPackageProspect(body) {
    return this.bffRequest('/admin/dmc/package-prospects', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateDmcPackageProspect(id, body) {
    return this.bffRequest(`/admin/dmc/package-prospects/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async deleteDmcPackageProspect(id) {
    return this.bffRequest(`/admin/dmc/package-prospects/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async importDmcPackageProspectsXlsx(file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.bffRequest('/admin/dmc/package-prospects/import/xlsx', { method: 'POST', body: formData });
  }

  static async exportDmcPackageProspectsCsv(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    const url = `${getBffBaseUrl()}/admin/dmc/package-prospects/export/csv${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'text/csv', ...this.adminBearerHeaders() },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Export failed');
    }
    return res.blob();
  }

  static async createVendorCampaign(body) {
    return this.bffRequest('/admin/campaign/create', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async listVendorCampaigns(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/admin/campaign/list${qs ? `?${qs}` : ''}`);
  }

  static async getVendorCampaignDetails(campaignId) {
    return this.bffRequest(`/admin/campaign/details/${encodeURIComponent(campaignId)}`);
  }

  static async resendVendorCampaignEmail(body) {
    return this.bffRequest('/admin/campaign/resend', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async cancelVendorCampaign(campaignId) {
    return this.bffRequest('/admin/campaign/cancel', {
      method: 'POST',
      body: JSON.stringify({ campaignId }),
    });
  }

  static async duplicateVendorCampaign(campaignId) {
    return this.bffRequest('/admin/campaign/duplicate', {
      method: 'POST',
      body: JSON.stringify({ campaignId }),
    });
  }

  static async previewVendorCampaignEmail(body) {
    return this.bffRequest('/admin/campaign/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async searchCampaignVendors(q, limit = 25) {
    const params = new URLSearchParams({ q: String(q || ''), limit: String(limit) });
    return this.bffRequest(`/admin/campaign/vendors/search?${params.toString()}`);
  }

  static vendorCampaignReportUrl(campaignId) {
    return `${getBffBaseUrl()}/admin/campaign/report/${encodeURIComponent(campaignId)}`;
  }

  static buildVendorCampaignSignupLink(campaign, mappingRow) {
    const base = publicSiteOrigin();
    const params = new URLSearchParams({
      campaign: campaign?.campaign_id || '',
      vendor: mappingRow?.vendor_ref || '',
      token: mappingRow?.tracking_token || '',
      source: 'email_campaign',
      utm_source: 'email',
      utm_medium: 'vendor_campaign',
      utm_campaign: String(campaign?.campaign_name || 'campaign')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_'),
    });
    return `${base}/vendor/attachment?${params.toString()}`;
  }

  /** Optional legacy header; login-as is now gated by admin auth on BFF. */
  static adminVendorHeaders() {
    const s =
      typeof import.meta !== 'undefined' && import.meta.env?.VITE_ADMIN_VENDOR_LOGIN_SECRET
        ? String(import.meta.env.VITE_ADMIN_VENDOR_LOGIN_SECRET).trim()
        : '';
    return s ? { 'X-Admin-Vendor-Secret': s } : {};
  }

  static hasAdminVendorSecret() {
    return true;
  }

  /** Returns vendor session token + login_url for vendor panel (Magento-style login-as). */
  static async vendorLoginAs(vendorId) {
    return this.bffRequest(`/admin/vendors/${encodeURIComponent(vendorId)}/login-as`, {
      method: 'POST',
      headers: { ...this.adminVendorHeaders() },
    });
  }

  /** Returns customer session token + login_url for website (Magento-style login-as). */
  static async customerLoginAs(customerId) {
    return this.bffRequest(`/admin/customers/${encodeURIComponent(customerId)}/login-as`, {
      method: 'POST',
      headers: { ...this.adminVendorHeaders() },
    });
  }

  /** PostgreSQL `system_settings` (key/value) via BFF pricing admin */
  static async getSystemSettings() {
    return this.bffRequest('/pricing/admin/system-settings');
  }

  static async createSystemSetting(input) {
    return this.bffRequest('/pricing/admin/system-settings', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateSystemSetting(id, input) {
    return this.bffRequest(`/pricing/admin/system-settings/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async graphqlRequest(query, variables = {}) {
    try {
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      return result.data;
    } catch (error) {
      log.error('GraphQL request failed:', error);
      throw error;
    }
  }

  // Static fallback method removed - now fully dynamic with database

  // 🚗 Vehicle Operations
  static async getVehicles(filters = {}) {
    try {
      const query = `
        query GetVehicles($status: String, $vendor_id: Int, $limit: Int, $offset: Int) {
          vehicles(status: $status, vendor_id: $vendor_id, limit: $limit, offset: $offset) {
            id
            vendor_id
            registration_number
            model
            brand
            year
            color
            fuel_type
            seating_capacity
            ac_available
            gps_enabled
            status
            current_location
            odometer_reading
            last_service_date
            next_service_date
            created_at
            updated_at
          }
        }
      `;

      return await this.graphqlRequest(query, filters);
    } catch (error) {
      log.error('Failed to fetch vehicles from database:', error.message);
      log.info('Falling back to static vehicle data...');

      // Return comprehensive static data when database is unavailable
      return {
        vehicles: [
          {
            id: 1,
            vendor_id: 1,
            registration_number: 'MH12AB1234',
            model: 'Swift Dzire',
            brand: 'Maruti',
            year: 2021,
            color: 'White',
            fuel_type: 'petrol',
            seating_capacity: 5,
            ac_available: true,
            gps_enabled: true,
            status: 'available',
            current_location: 'Pune',
            odometer_reading: 25000,
            last_service_date: '2024-01-15',
            next_service_date: '2024-07-15',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 2,
            vendor_id: 1,
            registration_number: 'MH12CD5678',
            model: 'Innova Crysta',
            brand: 'Toyota',
            year: 2020,
            color: 'Silver',
            fuel_type: 'diesel',
            seating_capacity: 7,
            ac_available: true,
            gps_enabled: true,
            status: 'available',
            current_location: 'Mumbai',
            odometer_reading: 45000,
            last_service_date: '2024-02-10',
            next_service_date: '2024-08-10',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 3,
            vendor_id: 1,
            registration_number: 'MH12EF9012',
            model: 'Ertiga',
            brand: 'Maruti',
            year: 2021,
            color: 'Blue',
            fuel_type: 'petrol',
            seating_capacity: 7,
            ac_available: true,
            gps_enabled: false,
            status: 'available',
            current_location: 'Kolhapur',
            odometer_reading: 32000,
            last_service_date: '2024-01-20',
            next_service_date: '2024-07-20',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 4,
            vendor_id: 2,
            registration_number: 'MH12GH3456',
            model: 'City',
            brand: 'Honda',
            year: 2022,
            color: 'Red',
            fuel_type: 'petrol',
            seating_capacity: 5,
            ac_available: true,
            gps_enabled: true,
            status: 'maintenance',
            current_location: 'Nashik',
            odometer_reading: 15000,
            last_service_date: '2024-03-01',
            next_service_date: '2024-09-01',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 5,
            vendor_id: 2,
            registration_number: 'MH12IJ7890',
            model: 'Scorpio',
            brand: 'Mahindra',
            year: 2020,
            color: 'Black',
            fuel_type: 'diesel',
            seating_capacity: 7,
            ac_available: true,
            gps_enabled: true,
            status: 'booked',
            current_location: 'Solapur',
            odometer_reading: 55000,
            last_service_date: '2024-02-28',
            next_service_date: '2024-08-28',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 6,
            vendor_id: 1,
            registration_number: 'MH12KL2468',
            model: 'Baleno',
            brand: 'Maruti',
            year: 2022,
            color: 'Grey',
            fuel_type: 'petrol',
            seating_capacity: 5,
            ac_available: true,
            gps_enabled: true,
            status: 'available',
            current_location: 'Pune',
            odometer_reading: 18000,
            last_service_date: '2024-03-10',
            next_service_date: '2024-09-10',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 7,
            vendor_id: 2,
            registration_number: 'MH12MN1357',
            model: 'Fortuner',
            brand: 'Toyota',
            year: 2021,
            color: 'White',
            fuel_type: 'diesel',
            seating_capacity: 7,
            ac_available: true,
            gps_enabled: true,
            status: 'available',
            current_location: 'Mumbai',
            odometer_reading: 28000,
            last_service_date: '2024-02-25',
            next_service_date: '2024-08-25',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ]
      };
    }
  }

  static async createVehicle(vehicleData) {
    const mutation = `
      mutation CreateVehicle($input: VehicleInput!) {
        createVehicle(input: $input) {
          id
          vendor_id
          registration_number
          model
          brand
          year
          color
          fuel_type
          seating_capacity
          ac_available
          gps_enabled
          status
          current_location
          created_at
          updated_at
        }
      }
    `;

    return this.graphqlRequest(mutation, { input: vehicleData });
  }

  static async updateVehicle(id, vehicleData) {
    const mutation = `
      mutation UpdateVehicle($id: Int!, $input: VehicleUpdateInput!) {
        updateVehicle(id: $id, input: $input) {
          id
          vendor_id
          registration_number
          model
          brand
          year
          color
          fuel_type
          seating_capacity
          ac_available
          gps_enabled
          status
          current_location
          updated_at
        }
      }
    `;

    return this.graphqlRequest(mutation, { id, input: vehicleData });
  }

  static async deleteVehicle(id) {
    const mutation = `
      mutation DeleteVehicle($id: Int!) {
        deleteVehicle(id: $id)
      }
    `;

    return this.graphqlRequest(mutation, { id });
  }

  // 👨‍💼 Driver Operations
  static async getDrivers(filters = {}) {
    const query = `
      query GetDrivers($status: String, $limit: Int, $offset: Int) {
        drivers(status: $status, limit: $limit, offset: $offset) {
          id
          user_id
          license_number
          license_expiry
          experience_years
          vehicle_assigned
          rating
          total_trips
          status
          languages_spoken
          background_verified
          medical_certificate_valid
          insurance_valid
          created_at
          updated_at
        }
      }
    `;

    return this.graphqlRequest(query, filters);
  }

  static async createDriver(driverData) {
    const mutation = `
      mutation CreateDriver($input: DriverInput!) {
        createDriver(input: $input) {
          id
          user_id
          license_number
          license_expiry
          experience_years
          status
          created_at
        }
      }
    `;

    return this.graphqlRequest(mutation, { input: driverData });
  }

  static async updateDriver(id, driverData) {
    const mutation = `
      mutation UpdateDriver($id: Int!, $input: DriverUpdateInput!) {
        updateDriver(id: $id, input: $input) {
          id
          license_number
          license_expiry
          experience_years
          vehicle_assigned
          status
          updated_at
        }
      }
    `;

    return this.graphqlRequest(mutation, { id, input: driverData });
  }

  static async deleteDriver(id) {
    const mutation = `
      mutation DeleteDriver($id: Int!) {
        deleteDriver(id: $id)
      }
    `;

    return this.graphqlRequest(mutation, { id });
  }

  // 📋 Booking Operations (admin: BFF REST; dashboard list → `getDashboard` via BFF near top)

  /** Create booking via BFF — body must match POST /api/v1/bookings validation */
  static async createBooking(payload) {
    return this.bffRequest('/bookings', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async updateBooking(id, bookingData) {
    const mutation = `
      mutation UpdateBooking($id: Int!, $input: BookingUpdateInput!) {
        updateBooking(id: $id, input: $input) {
          id
          driver_id
          vehicle_id
          status
          payment_status
          final_amount
          updated_at
        }
      }
    `;

    return this.graphqlRequest(mutation, { id, input: bookingData });
  }

  static async deleteBooking(id) {
    const mutation = `
      mutation DeleteBooking($id: Int!) {
        deleteBooking(id: $id)
      }
    `;

    return this.graphqlRequest(mutation, { id });
  }

  // 👥 User Operations
  /** Booking customers from `public.customers` (BFF GET /users — not admin `public.users`). */
  static async bffGetCustomer(id) {
    return this.bffRequest(`/users/${encodeURIComponent(id)}`);
  }

  static async bffUpdateCustomer(id, payload) {
    return this.bffRequest(`/users/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async bffUpdateCustomerStatus(id, status) {
    return this.bffRequest(`/users/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  static async bffListUsers(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    if (params.role && String(params.role).trim()) q.set('role', String(params.role).trim());
    if (params.status && String(params.status).trim()) q.set('status', String(params.status).trim().toLowerCase());
    if (params.registered_after && String(params.registered_after).trim()) {
      q.set('registered_after', String(params.registered_after).trim());
    }
    if (params.verified && String(params.verified).trim()) q.set('verified', String(params.verified).trim());
    const lim = Math.min(Math.max(parseInt(String(params.limit ?? 2000), 10) || 2000, 1), 5000);
    q.set('limit', String(lim));
    if (params.offset != null && params.offset !== '') q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.bffRequest(`/users${qs ? `?${qs}` : ''}`);
  }

  /**
   * Create or resolve a booking customer by email (`POST /users`).
   * Used for admin guest/walk-in bookings when no row exists yet (returns existing id if email matches).
   */
  static async bffEnsureBookingCustomer(payload) {
    return this.bffRequest('/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /** City / locality suggestions (`GET /locations/search`). */
  static async bffSearchLocations(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    const lim = Math.min(Math.max(parseInt(String(params.limit ?? 20), 10) || 20, 1), 50);
    q.set('limit', String(lim));
    if (params.include_localities !== undefined && params.include_localities !== '') {
      q.set('include_localities', String(!!params.include_localities));
    }
    if (params.trip_type && String(params.trip_type).trim()) q.set('trip_type', String(params.trip_type).trim());
    if (params.endpoint_role && String(params.endpoint_role).trim()) {
      q.set('endpoint_role', String(params.endpoint_role).trim());
    }
    if (params.parent_city && String(params.parent_city).trim()) q.set('parent_city', String(params.parent_city).trim());
    if (params.localities_only) q.set('localities_only', 'true');
    const qs = q.toString();
    return this.bffRequest(`/locations/search${qs ? `?${qs}` : ''}`);
  }

  /** Resolve lat/lng by place name without trip product flags (`is_oneway_to`, etc.). Admin booking fallback. */
  static async bffResolveLocationCoordinates(q) {
    const qs = new URLSearchParams();
    qs.set('q', String(q || '').trim());
    return this.bffRequest(`/locations/resolve-coordinates?${qs.toString()}`);
  }

  /**
   * Vehicle listing + fares (same contract as customer site `GET /api/cars/search`).
   * Dev: proxied via Vite `/customer-next-api` → customer Next.js (see vite.config.js).
   */
  static async customerCarsSearch(searchParams) {
    const qs =
      typeof searchParams === 'string'
        ? searchParams.replace(/^\?/, '')
        : new URLSearchParams(searchParams).toString();

    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
      try {
        const url = `/customer-next-api/api/cars/search?${qs}`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        const body = await res.json().catch(() => ({}));
        if (res.ok && body.success && body.data?.trip_details) {
          return body;
        }
      } catch {
        /* use BFF fallback when customer app is not running */
      }
    }

    return this.bffRequest(`/admin/cars-search?${qs}`);
  }

  /** Admin/staff from `public.users`. Driver roster is `public.drivers` (separate entity); not included here. */
  static async bffListStaffUsers(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    if (params.status && String(params.status).trim()) q.set('status', String(params.status).trim().toLowerCase());
    if (params.registered_after && String(params.registered_after).trim()) {
      q.set('registered_after', String(params.registered_after).trim());
    }
    const lim = Math.min(Math.max(parseInt(String(params.limit ?? 500), 10) || 500, 1), 2000);
    q.set('limit', String(lim));
    if (params.offset != null && params.offset !== '') q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.bffRequest(`/admin/staff-users${qs ? `?${qs}` : ''}`);
  }

  /** Admin/staff sign-in → `POST /admin/staff-login` (bcrypt password on `public.users`). */
  static async bffStaffLogin({
    email,
    password,
    turnstile_token: turnstileToken,
    captcha_token: captchaToken,
    recaptcha_token: recaptchaToken,
  }) {
    const token = captchaToken || turnstileToken || recaptchaToken;
    return this.bffRequest('/admin/staff-login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        _hp: '',
        ...(token
          ? {
              captcha_token: token,
              turnstile_token: token,
              recaptcha_token: token,
            }
          : {}),
      }),
    });
  }

  static async bffUpdateStaffProfile(id, input) {
    return this.bffRequest(`/admin/staff-users/${encodeURIComponent(id)}/profile`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async bffChangeStaffPassword({ email, current_password, new_password }) {
    return this.bffRequest('/admin/staff-change-password', {
      method: 'POST',
      body: JSON.stringify({ email, current_password, new_password }),
    });
  }

  /** Admin distance calculator — compare matrix, cache, routes, OSRM, haversine. */
  static async bffCalculateDistance({ from, to, forceRefresh = false }) {
    const q = new URLSearchParams();
    q.set('from', String(from).trim());
    q.set('to', String(to).trim());
    if (forceRefresh) q.set('force_refresh', 'true');
    return this.bffRequest(`/admin/distance/calculate?${q.toString()}`);
  }

  static async bffListDistanceCache(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    if (params.from_city && String(params.from_city).trim()) q.set('from_city', String(params.from_city).trim());
    if (params.to_city && String(params.to_city).trim()) q.set('to_city', String(params.to_city).trim());
    if (params.hub && params.hub !== 'all') q.set('hub', String(params.hub));
    if (params.min_km != null && params.min_km !== '') q.set('min_km', String(params.min_km));
    if (params.max_km != null && params.max_km !== '') q.set('max_km', String(params.max_km));
    const lim = Math.min(Math.max(parseInt(String(params.limit ?? 500), 10) || 500, 1), 2000);
    q.set('limit', String(lim));
    if (params.offset != null && params.offset !== '') q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.bffRequest(`/admin/distance/cache${qs ? `?${qs}` : ''}`);
  }

  static async bffImportDistanceCache(rows) {
    return this.bffRequest('/admin/distance/cache/import', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    });
  }

  static async bffUpsertDistanceCache(input) {
    return this.bffRequest('/admin/distance/cache', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async bffDeleteDistanceCache(id) {
    return this.bffRequest(`/admin/distance/cache/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async bffListDistanceMatrix(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    const lim = Math.min(Math.max(parseInt(String(params.limit ?? 200), 10) || 200, 1), 500);
    q.set('limit', String(lim));
    if (params.offset != null && params.offset !== '') q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.bffRequest(`/admin/distance/matrix${qs ? `?${qs}` : ''}`);
  }

  static async bffUpsertDistanceMatrix(input) {
    if (input.id != null && input.id !== '') {
      return this.bffRequest(`/admin/distance/matrix/${encodeURIComponent(input.id)}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      });
    }
    return this.bffRequest('/admin/distance/matrix', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async bffDeleteDistanceMatrix(id) {
    return this.bffRequest(`/admin/distance/matrix/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async bffGetDistanceEngineConfig() {
    return this.bffRequest('/admin/distance/engine-config');
  }

  static async bffSaveDistanceEngineConfig(config) {
    return this.bffRequest('/admin/distance/engine-config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  static async bffClearDistanceEngineMemoryCache() {
    return this.bffRequest('/admin/distance/engine-cache/clear', { method: 'POST' });
  }

  static async bffListDistanceProviders() {
    return this.bffRequest('/admin/distance/providers');
  }

  static async bffReorderDistanceProviders(payload) {
    return this.bffRequest('/admin/distance/providers/reorder', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async bffPatchDistanceProvider(providerCode, body) {
    return this.bffRequest(`/admin/distance/providers/${encodeURIComponent(providerCode)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async bffApplyDistanceProviderPreset(presetId) {
    return this.bffRequest(
      `/admin/distance/providers/presets/${encodeURIComponent(presetId)}`,
      { method: 'POST' },
    );
  }

  /** PostgreSQL `routes` table — city pairs, distance, waypoints (admin). */
  static async bffListTravelRoutes(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    const lim = Math.min(Math.max(parseInt(String(params.limit ?? 500), 10) || 500, 1), 2000);
    q.set('limit', String(lim));
    if (params.offset != null && params.offset !== '') q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.bffRequest(`/travel-routes${qs ? `?${qs}` : ''}`);
  }

  /** PMC-style tariff config routes (SEO landing pages, hub routes) */
  static async listConfigTariffRoutes(params = {}) {
    const q = new URLSearchParams();
    if (params.service_type) q.set('service_type', String(params.service_type));
    if (params.ensure_defaults) q.set('ensure_defaults', '1');
    const qs = q.toString();
    return this.bffRequest(`/admin/config/routes${qs ? `?${qs}` : ''}`);
  }

  static async updateConfigTariffRoute(id, input) {
    return this.bffRequest(`/admin/config/routes/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteConfigTariffRoute(id) {
    return this.bffRequest(`/admin/config/routes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async createConfigVehicleTariff(input) {
    return this.bffRequest('/admin/config/vehicle-tariffs', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateConfigVehicleTariff(id, input) {
    return this.bffRequest(`/admin/config/vehicle-tariffs/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteConfigVehicleTariff(id) {
    return this.bffRequest(`/admin/config/vehicle-tariffs/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async uploadConfigVehicleTariffImage(dataUrl) {
    return this.bffRequest('/admin/config/vehicle-tariffs/upload-image', {
      method: 'POST',
      body: JSON.stringify({ dataUrl }),
    });
  }

  static async getConfigTariffRoute(id) {
    return this.bffRequest(`/admin/config/routes/${encodeURIComponent(id)}`);
  }

  static async getConfigRouteSeoAudit(id) {
    return this.bffRequest(`/admin/routes/${encodeURIComponent(id)}/seo-audit`);
  }

  static async getConfigTariffServiceTypes() {
    return this.bffRequest('/admin/config/service-types');
  }

  static async getConfigVehicleTariffs() {
    return this.bffRequest('/admin/config/vehicle-tariffs');
  }

  static async saveConfigVehicleSurgeCharges(items) {
    return this.bffRequest('/admin/config/vehicle-tariffs/surge-charges', {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  }

  static async saveConfigTariffServiceFares(routeId, serviceType, fares) {
    return this.bffRequest(`/admin/config/routes/${encodeURIComponent(routeId)}/service-fares`, {
      method: 'PUT',
      body: JSON.stringify({ service_type: serviceType, fares }),
    });
  }

  static async exportConfigPerRouteTariffsCsv(params = {}) {
    const q = new URLSearchParams();
    if (params.all) q.set('all', '1');
    if (params.routeIds?.length) q.set('route_ids', params.routeIds.join(','));
    if (params.serviceTypes?.length) q.set('service_types', params.serviceTypes.join(','));
    const qs = q.toString();
    const url = `${getBffBaseUrl()}/admin/config/routes/export/per-route-tariffs/csv${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: {
        Accept: 'text/csv',
        ...this.adminBearerHeaders(),
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (
        this.isBffUnauthorized(res.status, body, '/admin/config/routes/export/per-route-tariffs/csv')
      ) {
        this.clearAdminSession();
      }
      throw new Error(body.message || body.error || 'Export failed');
    }
    return res.blob();
  }

  static async previewCloneConfigTariffServiceFares(routeId, serviceType) {
    const q = serviceType ? `?service_type=${encodeURIComponent(serviceType)}` : '';
    return this.bffRequest(
      `/admin/config/routes/${encodeURIComponent(routeId)}/clone-service-fares/preview${q}`,
    );
  }

  static async cloneConfigTariffServiceFares(routeId, body) {
    return this.bffRequest(`/admin/config/routes/${encodeURIComponent(routeId)}/clone-service-fares`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async listConfigUnsetTariffRoutes(params = {}) {
    const q = new URLSearchParams();
    if (params.service_type) q.set('service_type', params.service_type);
    if (params.q) q.set('q', params.q);
    if (params.hub) q.set('hub', params.hub);
    if (params.active != null && params.active !== '') q.set('active', params.active);
    if (params.slug) q.set('slug', params.slug);
    if (params.to_city) q.set('to_city', params.to_city);
    const qs = q.toString();
    return this.bffRequest(`/admin/config/routes/unset-tariffs${qs ? `?${qs}` : ''}`);
  }

  static async saveConfigLegacyRouteFares(routeId, fares) {
    return this.bffRequest(`/admin/config/routes/${encodeURIComponent(routeId)}/fares`, {
      method: 'PUT',
      body: JSON.stringify({ fares }),
    });
  }

  static async getConfigCityTariffsMeta() {
    return this.bffRequest('/admin/config/city-tariffs/meta');
  }

  static async getConfigCityTariffs(category, hub) {
    const q = hub ? `?hub=${encodeURIComponent(hub)}` : '';
    return this.bffRequest(`/admin/config/city-tariffs/${encodeURIComponent(category)}${q}`);
  }

  static async saveConfigCityTariffs(category, hub, fares) {
    return this.bffRequest(`/admin/config/city-tariffs/${encodeURIComponent(category)}`, {
      method: 'PUT',
      body: JSON.stringify({ hub, fares }),
    });
  }

  static async applyConfigCityTariffs(category, hub) {
    return this.bffRequest(`/admin/config/city-tariffs/${encodeURIComponent(category)}/apply`, {
      method: 'POST',
      body: JSON.stringify({ hub }),
    });
  }

  static async createConfigTariffRoute(input) {
    return this.bffRequest('/admin/config/routes', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async setConfigTariffRouteActive(id, isActive) {
    return this.bffRequest(`/admin/config/routes/${encodeURIComponent(id)}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: isActive ? 1 : 0 }),
    });
  }

  static async setConfigTariffRouteSitemap(id, includeInSitemap) {
    return this.bffRequest(`/admin/config/routes/${encodeURIComponent(id)}/sitemap`, {
      method: 'PATCH',
      body: JSON.stringify({ include_in_sitemap: includeInSitemap ? 1 : 0 }),
    });
  }

  static async setConfigTariffRouteSurge(id, surgeEnabled) {
    return this.bffRequest(`/admin/config/routes/${encodeURIComponent(id)}/surge`, {
      method: 'PATCH',
      body: JSON.stringify({ surge_enabled: surgeEnabled ? 1 : 0 }),
    });
  }

  static async bulkConfigTariffRoutesAction(ids, action) {
    return this.bffRequest('/admin/config/routes/bulk', {
      method: 'POST',
      body: JSON.stringify({ ids, action }),
    });
  }

  static async seedConfigTariffRoutes(mode) {
    const path =
      mode === 'corridor'
        ? '/admin/config/routes/seed-corridor-seo'
        : mode === 'destinations'
          ? '/admin/config/routes/seed-destination-seo'
          : '/admin/config/routes/seed-seo-all';
    return this.bffRequest(path, { method: 'POST' });
  }

  static async importConfigTariffStaticPages({ dryRun = false, deleteAfter = false } = {}) {
    return this.bffRequest('/admin/config/routes/import-static-pages', {
      method: 'POST',
      body: JSON.stringify({
        dry_run: dryRun,
        delete_after: deleteAfter,
      }),
    });
  }

  static async exportConfigTariffRoutesCsv(params = {}) {
    const q = new URLSearchParams();
    if (params.q) q.set('q', params.q);
    if (params.hub) q.set('hub', params.hub);
    if (params.active != null && params.active !== '') q.set('active', params.active);
    if (params.slug) q.set('slug', params.slug);
    if (params.ids) q.set('ids', params.ids);
    const qs = q.toString();
    const url = `${getBffBaseUrl()}/admin/config/routes/export/csv${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: {
        Accept: 'text/csv',
        ...this.adminBearerHeaders(),
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (this.isBffUnauthorized(res.status, body, '/admin/config/routes/export/csv')) {
        this.clearAdminSession();
      }
      throw new Error(body.message || body.error || 'Export failed');
    }
    return res.blob();
  }

  static async getConfigTariffLocalityRoutes(masterRouteId) {
    return this.bffRequest(`/admin/config/routes/${encodeURIComponent(masterRouteId)}/locality-routes`);
  }

  static async createConfigTariffLocalityRoutes(masterRouteId) {
    return this.bffRequest(`/admin/config/routes/${encodeURIComponent(masterRouteId)}/locality-routes`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  /** Save enable/disable + additional charge for localities under a master route. */
  static async saveConfigTariffMasterLocalities(masterRouteId, localities) {
    return this.bffRequest(`/admin/config/routes/${encodeURIComponent(masterRouteId)}/master-localities`, {
      method: 'POST',
      body: JSON.stringify({ localities }),
    });
  }

  /** Dynamic cities from BFF (`/cities`) */
  static async bffListCities(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    if (params.page != null && params.page !== '') q.set('page', String(params.page));
    if (params.limit != null && params.limit !== '') q.set('limit', String(params.limit));
    if (params.include_localities != null) q.set('include_localities', String(!!params.include_localities));
    if (params.include_inactive != null) q.set('include_inactive', String(!!params.include_inactive));
    const gridKeys = [
      'filter_type',
      'filter_id',
      'filter_name',
      'filter_state',
      'filter_district',
      'filter_place_type',
      'filter_iso',
      'filter_status',
      'filter_flag',
    ];
    for (const key of gridKeys) {
      const val = params[key];
      if (val != null && String(val).trim()) q.set(key, String(val).trim());
    }
    const qs = q.toString();
    return this.bffRequest(`/cities${qs ? `?${qs}` : ''}`);
  }

  static async bffListStates() {
    return this.bffRequest('/states');
  }

  static async bffCreateCity(input) {
    return this.bffRequest('/cities', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async bffUpdateCity(id, input) {
    return this.bffRequest(`/cities/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async bffGetCityServiceability(id) {
    return this.bffRequest(`/admin/cities/${encodeURIComponent(id)}/serviceability`);
  }

  static async bffPutCityServiceability(id, services) {
    return this.bffRequest(`/admin/cities/${encodeURIComponent(id)}/serviceability`, {
      method: 'PUT',
      body: JSON.stringify({ services }),
    });
  }

  static async bffListLocationPricing({ localityId, cityId, cityDefaults, status } = {}) {
    const q = new URLSearchParams();
    if (localityId != null) q.set('locality_id', String(localityId));
    if (cityId != null) q.set('city_id', String(cityId));
    if (cityDefaults) q.set('city_defaults', '1');
    if (status) q.set('status', status);
    const qs = q.toString();
    return this.bffRequest(`/admin/location-pricing${qs ? `?${qs}` : ''}`);
  }

  static async bffUpsertLocationPricing(input) {
    return this.bffRequest('/admin/location-pricing', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async bffDeleteLocationPricing(id) {
    return this.bffRequest(`/admin/location-pricing/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async bffResolveLocationPricing(params) {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    return this.bffRequest(`/admin/location-pricing/resolve?${q.toString()}`);
  }

  static async bffListLocationPincodes(localityId) {
    const q = new URLSearchParams();
    q.set('locality_id', String(localityId));
    return this.bffRequest(`/admin/location-pincodes?${q.toString()}`);
  }

  static async bffUpsertLocationPincode(input) {
    return this.bffRequest('/admin/location-pincodes', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async bffDeleteLocationPincode(id) {
    return this.bffRequest(`/admin/location-pincodes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async bffListLocationRelationships({ entityType, entityId, as } = {}) {
    const q = new URLSearchParams();
    if (entityType) q.set('entity_type', entityType);
    if (entityId != null) q.set('entity_id', String(entityId));
    if (as) q.set('as', as);
    return this.bffRequest(`/admin/location-relationships?${q.toString()}`);
  }

  static async bffCreateLocationRelationship(input) {
    return this.bffRequest('/admin/location-relationships', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async bffDeleteLocationRelationship(id) {
    return this.bffRequest(`/admin/location-relationships/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async bffGetLocalityGeo(id) {
    return this.bffRequest(`/admin/localities/${encodeURIComponent(id)}/geo`);
  }

  static async bffSyncLocationGeo(input = {}) {
    return this.bffRequest('/admin/location-geo/sync', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async bffGetLocationSearchIndexStatus() {
    return this.bffRequest('/admin/location-search-index/status');
  }

  static async bffSyncLocationSearchIndex(input = {}) {
    return this.bffRequest('/admin/location-search-index/sync', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async bffGetLocationMasterOpsStatus(params = {}) {
    const q = new URLSearchParams();
    if (params.city) q.set('city', params.city);
    if (params.city_id) q.set('city_id', String(params.city_id));
    const qs = q.toString();
    return this.bffRequest(`/admin/location-master-ops/status${qs ? `?${qs}` : ''}`);
  }

  static async bffSyncLocationMasterOps(input = {}) {
    return this.bffRequest('/admin/location-master-ops/sync', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async bffClearLocationSearchCache(input = {}) {
    return this.bffRequest('/admin/location-master-ops/clear-search-cache', {
      method: 'POST',
      body: JSON.stringify({ city: input.city || undefined }),
    });
  }

  static async bffDeleteCity(id) {
    return this.bffRequest(`/cities/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** Duplicate active "City" hubs (same name + state) across the whole cities table. */
  static async bffListCityDuplicates() {
    return this.bffRequest('/cities/duplicates');
  }

  /** Deactivate the non-kept rows of a duplicate group (admin-only cleanup). */
  static async bffResolveCityDuplicates({ keep_id, remove_ids }) {
    return this.bffRequest('/cities/duplicates/resolve', {
      method: 'POST',
      body: JSON.stringify({ keep_id, remove_ids }),
    });
  }

  /** Localities for a city (`localities` table; linked by city name + state). */
  static async bffListLocalitiesForCity(cityId) {
    const q = new URLSearchParams();
    q.set('city_id', String(cityId));
    return this.bffRequest(`/localities?${q.toString()}`);
  }

  static async bffCreateLocality(input) {
    return this.bffRequest('/localities', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async bffUpdateLocality(id, input) {
    return this.bffRequest(`/localities/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async bffPatchLocalitySeo(id, input) {
    return this.bffRequest(`/admin/localities/${encodeURIComponent(id)}/seo`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  static async bffDeleteLocality(id) {
    return this.bffRequest(`/localities/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async bffListLocationAliases({ entityType = 'locality', entityId }) {
    const q = new URLSearchParams();
    q.set('entity_type', entityType);
    q.set('entity_id', String(entityId));
    return this.bffRequest(`/admin/location-aliases?${q.toString()}`);
  }

  static async bffCreateLocationAlias(input) {
    return this.bffRequest('/admin/location-aliases', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async bffDeleteLocationAlias(id) {
    return this.bffRequest(`/admin/location-aliases/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async bffListLocationSearchMisses({ limit = 50, city, status = 'open' } = {}) {
    const q = new URLSearchParams();
    q.set('limit', String(limit));
    if (city) q.set('city', city);
    if (status) q.set('status', status);
    return this.bffRequest(`/admin/location-search-misses?${q.toString()}`);
  }

  static async bffDeleteLocationSearchMiss(id) {
    return this.bffRequest(`/admin/location-search-misses/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async bffClearLocationSearchMisses() {
    return this.bffRequest('/admin/location-search-misses', { method: 'DELETE' });
  }

  static async bffPromoteLocationSearchMiss(id, body = {}) {
    return this.bffRequest(`/admin/location-search-misses/${encodeURIComponent(id)}/promote`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async bffPromoteLocationSearchMissBatch(body = {}) {
    return this.bffRequest('/admin/location-search-misses/promote-batch', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async bffCreateTravelRoute(input) {
    return this.bffRequest('/travel-routes', { method: 'POST', body: JSON.stringify(input) });
  }

  static async bffUpdateTravelRoute(id, input) {
    return this.bffRequest(`/travel-routes/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async bffDeleteTravelRoute(id) {
    return this.bffRequest(`/travel-routes/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** Customer segment definitions (`customer_segments`). */
  static async bffListCustomerSegments(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    q.set('limit', String(Math.min(Math.max(parseInt(String(params.limit ?? 200), 10) || 200, 1), 1000)));
    if (params.offset != null && params.offset !== '') q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.bffRequest(`/customer-segments${qs ? `?${qs}` : ''}`);
  }

  static async bffCreateCustomerSegment(input) {
    return this.bffRequest('/customer-segments', { method: 'POST', body: JSON.stringify(input) });
  }

  static async bffUpdateCustomerSegment(id, input) {
    return this.bffRequest(`/customer-segments/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async bffDeleteCustomerSegment(id) {
    return this.bffRequest(`/customer-segments/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** User ↔ segment assignments (`user_segments`). */
  static async bffListUserSegments(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    q.set('limit', String(Math.min(Math.max(parseInt(String(params.limit ?? 500), 10) || 500, 1), 2000)));
    if (params.offset != null && params.offset !== '') q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.bffRequest(`/user-segments${qs ? `?${qs}` : ''}`);
  }

  static async bffAssignUserSegment(input) {
    return this.bffRequest('/user-segments', { method: 'POST', body: JSON.stringify(input) });
  }

  static async bffDeleteUserSegmentAssignment(id) {
    return this.bffRequest(`/user-segments/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** Cab segment catalog (`cab_segments` in PostgreSQL). */
  static async bffListCabSegments(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    if (params.all) q.set('all', '1');
    if (params.lite) q.set('lite', '1');
    const qs = q.toString();
    return this.bffRequest(`/cab-segments${qs ? `?${qs}` : ''}`);
  }

  static async bffGetCabSegment(id) {
    return this.bffRequest(`/cab-segments/${encodeURIComponent(id)}`);
  }

  static async bffCreateCabSegment(input) {
    return this.bffRequest('/cab-segments', { method: 'POST', body: JSON.stringify(input) });
  }

  static async bffUpdateCabSegment(id, input) {
    return this.bffRequest(`/cab-segments/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async bffDeleteCabSegment(id) {
    return this.bffRequest(`/cab-segments/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** Deletes all rows and inserts built-in defaults (requires confirm token). */
  static async bffReseedCabSegmentsDefaults() {
    return this.bffRequest('/cab-segments/reseed-defaults', {
      method: 'POST',
      body: JSON.stringify({ confirm: 'reset-all-cab-segments' }),
    });
  }

  /** Fleet → Sold Out (`cab_sold_out` table): segment unavailable on a date in all/selected cities. */
  static async bffListCabSoldOut(params = {}) {
    const q = new URLSearchParams();
    if (params.from && String(params.from).trim()) q.set('from', String(params.from).trim());
    if (params.to && String(params.to).trim()) q.set('to', String(params.to).trim());
    if (params.segment_id && String(params.segment_id).trim()) q.set('segment_id', String(params.segment_id).trim());
    const qs = q.toString();
    return this.bffRequest(`/cab-sold-out${qs ? `?${qs}` : ''}`);
  }

  static async bffCreateCabSoldOut(input) {
    return this.bffRequest('/cab-sold-out', { method: 'POST', body: JSON.stringify(input) });
  }

  static async bffUpdateCabSoldOut(id, input) {
    return this.bffRequest(`/cab-sold-out/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async bffDeleteCabSoldOut(id) {
    return this.bffRequest(`/cab-sold-out/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async getCancellationConfig() {
    return this.bffRequest('/pricing/admin/cancellation-config');
  }

  static async saveCancellationConfig(config) {
    return this.bffRequest('/pricing/admin/cancellation-config', {
      method: 'PUT',
      body: JSON.stringify({ config }),
    });
  }

  static async getCancellationInsights(days = 90, options = {}) {
    const d = Math.min(Math.max(parseInt(String(days), 10) || 90, 7), 365);
    const q = new URLSearchParams({ days: String(d) });
    if (options.cancelledBy) q.set('cancelled_by', String(options.cancelledBy));
    return this.bffRequest(`/analytics/cancellations?${q.toString()}`);
  }

  static async getCouponUsageReport(days = 30, options = {}) {
    const d = Math.min(Math.max(parseInt(String(days), 10) || 30, 7), 365);
    const q = new URLSearchParams({ days: String(d) });
    if (options.status) q.set('status', String(options.status));
    return this.bffRequest(`/analytics/coupon-usage?${q.toString()}`);
  }

  /** Master toggle: show coupon field on customer checkout */
  static async getCouponMasterSettings() {
    return this.bffRequest('/admin/settings/coupons');
  }

  static async saveCouponMasterSettings(enabled) {
    return this.bffRequest('/admin/settings/coupons', {
      method: 'PUT',
      body: JSON.stringify({ enabled: Boolean(enabled) }),
    });
  }

  static async getMaintenanceModeSettings() {
    return this.bffRequest('/admin/settings/maintenance-mode');
  }

  static async getAdminIpAllowlistSettings() {
    return this.bffRequest('/admin/settings/admin-ip-allowlist');
  }

  static async saveAdminIpAllowlistSettings(payload) {
    return this.bffRequest('/admin/settings/admin-ip-allowlist', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async getAttackProtectionSettings() {
    return this.bffRequest('/admin/settings/attack-protection');
  }

  static async saveAttackProtectionSettings(payload) {
    return this.bffRequest('/admin/settings/attack-protection', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async getAdminActiveSessions(query = '') {
    return this.bffRequest(`/admin/security/sessions${query}`);
  }

  static async revokeAdminSession(sessionId) {
    return this.bffRequest(`/admin/security/sessions/${sessionId}`, { method: 'DELETE' });
  }

  /** Read-only PostgreSQL status (migrations, table counts, connection). */
  static async getDatabaseStatus() {
    return this.bffRequest('/admin/database/status');
  }

  static async getAiSeoStatus() {
    return this.bffRequest('/admin/ai-seo/status');
  }

  static async aiSeo(path, options = {}) {
    const suffix = path.startsWith('/') ? path : `/${path}`;
    return this.bffRequest(`/admin/ai-seo${suffix}`, options);
  }

  // ---- Vehicle Landing Pages CMS ----
  static async listVehicleLandingPages(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/vehicle-landings/admin${qs ? `?${qs}` : ''}`);
  }

  static async getVehicleLandingPage(id) {
    return this.bffRequest(`/vehicle-landings/admin/${encodeURIComponent(id)}`);
  }

  static async createVehicleLandingPage(body) {
    return this.bffRequest('/vehicle-landings/admin', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateVehicleLandingPage(id, body) {
    return this.bffRequest(`/vehicle-landings/admin/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async deleteVehicleLandingPage(id) {
    return this.bffRequest(`/vehicle-landings/admin/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async duplicateVehicleLandingPage(id) {
    return this.bffRequest(`/vehicle-landings/admin/${encodeURIComponent(id)}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async bulkVehicleLandingPages(action, ids) {
    return this.bffRequest('/vehicle-landings/admin/bulk', {
      method: 'POST',
      body: JSON.stringify({ action, ids }),
    });
  }

  static async listVehicleLandingCategories() {
    return this.bffRequest('/vehicle-landings/admin/categories');
  }

  static async createVehicleLandingCategory(body) {
    return this.bffRequest('/vehicle-landings/admin/categories', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateVehicleLandingCategory(id, body) {
    return this.bffRequest(`/vehicle-landings/admin/categories/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async listVehicleLandingVehicles(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/vehicle-landings/admin/vehicles${qs ? `?${qs}` : ''}`);
  }

  static async createVehicleLandingVehicle(body) {
    return this.bffRequest('/vehicle-landings/admin/vehicles', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateVehicleLandingVehicle(id, body) {
    return this.bffRequest(`/vehicle-landings/admin/vehicles/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async uploadVehicleLandingImage(file) {
    const fd = new FormData();
    fd.append('image', file);
    return this.bffRequest('/vehicle-landings/admin/upload-image', {
      method: 'POST',
      body: fd,
    });
  }

  static async exportVehicleLandingPagesCsv(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    q.set('all', '1');
    const qs = q.toString();
    const url = `${getBffBaseUrl()}/vehicle-landings/admin/export/csv${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { ...this.adminBearerHeaders() },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `Export failed (${res.status})`);
    }
    return res.blob();
  }

  static async importVehicleLandingPagesCsv(csvText) {
    return this.bffRequest('/vehicle-landings/admin/import/csv', {
      method: 'POST',
      body: JSON.stringify({ csv: csvText }),
    });
  }

  static async listDbBackups() {
    return this.bffRequest('/admin/db-backups');
  }

  static async getCacheManagementStatus() {
    return this.bffRequest('/admin/cache-management');
  }

  static async clearCacheManagement(payload = {}) {
    return this.bffRequest('/admin/cache-management/clear', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async purgeCacheEdge(payload = {}) {
    return this.bffRequest('/admin/cache-management/purge-edge', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  static async createDbBackup() {
    return this.bffRequest('/admin/db-backups', { method: 'POST', body: JSON.stringify({}) });
  }

  static async getDbBackupSettings() {
    return this.bffRequest('/admin/db-backups/settings');
  }

  static async saveDbBackupSettings(payload = {}) {
    return this.bffRequest('/admin/db-backups/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async downloadDbBackup(filename) {
    const name = encodeURIComponent(String(filename || ''));
    const url = `${getBffBaseUrl()}/admin/db-backups/${name}/download`;
    const res = await fetch(url, {
      credentials: 'include',
      cache: 'no-store',
      headers: {
        Accept: 'application/gzip',
        ...this.adminBearerHeaders(),
      },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      if (this.isBffUnauthorized(res.status, err, '/admin/db-backups/download')) {
        this.clearAdminSession();
      }
      throw new Error(err.message || err.error || `Download failed (${res.status})`);
    }
    return res.blob();
  }

  static async saveMaintenanceModeSettings(payload) {
    return this.bffRequest('/admin/settings/maintenance-mode', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async getMobileAppSettings() {
    return this.bffRequest('/admin/settings/mobile-app');
  }

  static async saveMobileAppSettings(payload) {
    return this.bffRequest('/admin/settings/mobile-app', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async getWebsiteDesignSettings() {
    return this.bffRequest('/admin/settings/website-design');
  }

  static async saveWebsiteDesignSettings(payload) {
    return this.bffRequest('/admin/settings/website-design', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async getWebsiteSchemaSettings() {
    return this.bffRequest('/admin/settings/website-schema');
  }

  static async saveWebsiteSchemaSettings(payload) {
    return this.bffRequest('/admin/settings/website-schema', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async getDriverAppSettings() {
    return this.bffRequest('/admin/settings/driver-app');
  }

  static async saveDriverAppSettings(payload) {
    return this.bffRequest('/admin/settings/driver-app', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async getVendorOnboardingFormSettings() {
    return this.bffRequest('/admin/settings/vendor-onboarding-form');
  }

  static async saveVendorOnboardingFormSettings(payload) {
    return this.bffRequest('/admin/settings/vendor-onboarding-form', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  static async getCookieConsentSettings() {
    return this.bffRequest('/admin/settings/cookie-consent');
  }

  static async saveCookieConsentSettings(payload) {
    const body =
      typeof payload === 'boolean'
        ? { enabled: payload }
        : {
            ...(payload?.enabled !== undefined ? { enabled: Boolean(payload.enabled) } : {}),
            ...(payload?.title !== undefined ? { title: String(payload.title) } : {}),
            ...(payload?.body !== undefined ? { body: String(payload.body) } : {}),
            ...(payload?.banner_delay_seconds !== undefined
              ? { banner_delay_seconds: Number(payload.banner_delay_seconds) }
              : {}),
          };
    return this.bffRequest('/admin/settings/cookie-consent', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async getCookieConsentReport(days = 30, options = {}) {
    const d = Math.min(Math.max(parseInt(String(days), 10) || 30, 1), 365);
    const q = new URLSearchParams({ days: String(d) });
    if (options.userType) q.set('user_type', String(options.userType));
    if (options.action) q.set('action', String(options.action));
    if (options.analytics) q.set('analytics', String(options.analytics));
    if (options.marketing) q.set('marketing', String(options.marketing));
    if (options.search) q.set('search', String(options.search));
    if (options.page) q.set('page', String(options.page));
    if (options.limit) q.set('limit', String(options.limit));
    return this.bffRequest(`/analytics/cookie-consent?${q.toString()}`);
  }

  static async exportCookieConsentReport(days = 30, options = {}) {
    const d = Math.min(Math.max(parseInt(String(days), 10) || 30, 1), 365);
    const q = new URLSearchParams({ days: String(d) });
    if (options.userType) q.set('user_type', String(options.userType));
    if (options.action) q.set('action', String(options.action));
    if (options.analytics) q.set('analytics', String(options.analytics));
    if (options.marketing) q.set('marketing', String(options.marketing));
    if (options.search) q.set('search', String(options.search));

    const url = `${getBffBaseUrl()}/analytics/cookie-consent/export?${q.toString()}`;
    const res = await fetch(url, {
      headers: {
        Accept: 'text/csv',
        ...this.adminBearerHeaders(),
      },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      if (this.isBffUnauthorized(res.status, err, '/analytics/cookie-consent/export')) {
        this.clearAdminSession();
      }
      throw new Error(err.message || `Export failed (${res.status})`);
    }
    return res.blob();
  }

  /** Aggregated analytics for admin dashboard (bookings, vendors, daily series). */
  static async bffAnalyticsOverview(days = 30) {
    const d = Math.min(Math.max(parseInt(String(days), 10) || 30, 1), 366);
    return this.bffRequest(`/analytics/overview?days=${d}`);
  }

  static async bffBookingFunnel(path, params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== '') query.set(key, String(value));
    });
    const qs = query.toString();
    return this.bffRequest(`/admin/analytics/${path}${qs ? `?${qs}` : ''}`);
  }

  /** Google Ads growth platform (`/api/v1/admin/marketing/*` on BFF) */
  static async bffMarketing(path, params = {}, options = {}) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== '') query.set(key, String(value));
    });
    const qs = query.toString();
    const method = options.method || 'GET';
    const init = { method };
    if (options.body != null) {
      init.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
    }
    return this.bffRequest(`/admin/marketing/${path}${qs ? `?${qs}` : ''}`, init);
  }

  static async bffMarketingDashboard(params = {}) {
    return this.bffMarketing('dashboard', params);
  }

  static async bffMarketingCampaigns(params = {}) {
    return this.bffMarketing('campaigns', params);
  }

  static async bffMarketingSaveCampaign(payload, id) {
    if (id) return this.bffMarketing(`campaigns/${id}`, {}, { method: 'PATCH', body: payload });
    return this.bffMarketing('campaigns', {}, { method: 'POST', body: payload });
  }

  static async bffMarketingKeywords(params = {}) {
    return this.bffMarketing('keywords', params);
  }

  static async bffMarketingLandingPages() {
    return this.bffMarketing('landing-pages');
  }

  static async bffMarketingAttribution(params = {}) {
    return this.bffMarketing('attribution', params);
  }

  static async bffMarketingLinkLead(body) {
    return this.bffMarketing('attribution/link', {}, { method: 'POST', body });
  }

  static async bffMarketingSpendPreview(csv) {
    return this.bffMarketing('import/ad-spend/preview', {}, { method: 'POST', body: { csv } });
  }

  static async bffMarketingSpendImport(csv) {
    return this.bffMarketing('import/ad-spend', {}, { method: 'POST', body: { csv } });
  }

  static async bffMarketingReport(type, params = {}) {
    return this.bffMarketing(`reports/${type}`, params);
  }

  static async bffMarketingRecommendations(params = {}) {
    return this.bffMarketing('recommendations', params);
  }

  static async bffMarketingGoogleAdsStatus() {
    return this.bffMarketing('integrations/google-ads/status');
  }

  static async bffMarketingGoogleAdsSync() {
    return this.bffMarketing('integrations/google-ads/sync', {}, { method: 'POST', body: {} });
  }

  static async bffMarketingConversionTracking() {
    return this.bffMarketing('conversion-tracking');
  }

  static async bffMarketingAuditLogs(params = {}) {
    return this.bffMarketing('audit-logs', params);
  }

  /** AI Search Visibility (`/api/v1/admin/ai-visibility/*`) */
  static async bffAiVisibility(path, params = {}, options = {}) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== '') query.set(key, String(value));
    });
    const qs = query.toString();
    const method = options.method || 'GET';
    const init = { method };
    if (options.body != null) {
      init.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
    }
    return this.bffRequest(`/admin/ai-visibility/${path}${qs ? `?${qs}` : ''}`, init);
  }

  static async bffLinkFunnelJourney(journeyId, bookingId) {
    return this.bffRequest('/admin/analytics/journeys/link-booking', {
      method: 'POST',
      body: JSON.stringify({ journey_id: journeyId, booking_id: Number(bookingId) }),
    });
  }

  static getBffBaseUrl() {
    return resolveBffBaseUrl();
  }

  /** Latest Playwright JSON report + run history (qualityvalidation/reports). */
  static async bffQaTestResults() {
    return this.bffRequest('/admin/qa-test-results');
  }

  /** Remove selected Playwright test cases from the on-disk results JSON. */
  static async bffQaTestResultsBulkDelete(ids) {
    return this.bffRequest('/admin/qa-test-results/tests/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  /** Short-lived token to open Playwright HTML report in a new tab. */
  static async bffQaTestReportToken() {
    return this.bffRequest('/admin/qa-test-results/report-token', { credentials: 'include' });
  }

  /**
   * PostHog / DB metrics on customer Next.js `/api/admin/analytics/*`.
   * Dev: same-origin path (Vite proxies to Next — see vite.config.js).
   * Staging/prod: absolute URL on the customer origin (admin host has no such API;
   * SPA fallback would return HTML and surface as "Overview failed").
   */
  static async posthogAnalyticsRequest(endpoint, params = {}) {
    const q = new URLSearchParams(params).toString();
    const path = `/api/admin/analytics${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    const isDev = typeof import.meta !== 'undefined' && Boolean(import.meta.env?.DEV);
    const customerOrigin = isDev ? '' : publicSiteOrigin().replace(/\/$/, '');
    const url = `${customerOrigin}${path}${q ? `?${q}` : ''}`;
    const res = await fetch(url, {
      // Auth is via staff Bearer JWT; omit cookies so CORS can stay credentials=false.
      // Use Fetch `cache: 'no-store'` only — a Cache-Control request header is not
      // CORS-safelisted and was blocked by customer preflight Allow-Headers.
      credentials: 'omit',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        ...this.adminBearerHeaders(),
      },
    });
    const contentType = res.headers.get('content-type') || '';
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = body.error || body.message || res.statusText || 'Analytics request failed';
      throw new Error(res.status === 401 ? `Unauthorized — sign in to admin or set analytics secret. ${msg}` : msg);
    }
    if (!contentType.includes('application/json') || body.success == null) {
      throw new Error(
        customerOrigin
          ? 'Analytics API did not return JSON from the customer app. Confirm VITE_CUSTOMER_APP_URL points at Next.js.'
          : 'Analytics API is not available on this host. Start the customer Next.js app or configure the Vite proxy.',
      );
    }
    return body;
  }

  /** SEO Route Analytics endpoints */
  static async getSEORouteAnalytics(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    if (params.status) q.set('status', params.status);
    q.set('limit', String(Math.min(Math.max(parseInt(String(params.limit ?? 500), 10) || 500, 1), 2000)));
    if (params.offset != null && params.offset !== '') q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.bffRequest(`/seo-route-analytics${qs ? `?${qs}` : ''}`);
  }

  static async createSEORouteAnalytics(input) {
    return this.bffRequest('/seo-route-analytics', { method: 'POST', body: JSON.stringify(input) });
  }

  static async updateSEORouteAnalytics(id, input) {
    return this.bffRequest(`/seo-route-analytics/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteSEORouteAnalytics(id) {
    return this.bffRequest(`/seo-route-analytics/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** Import rows from frontend-nextjs/Seo/*.csv into seo_route_analytics (BFF reads file on server). */
  static async importSeoRouteCsv(file) {
    return this.bffRequest('/seo-route-analytics/import-csv', {
      method: 'POST',
      body: JSON.stringify({ file: String(file || '').trim() }),
    });
  }

  static async analyzeSEORoute(url) {
    return this.bffRequest('/seo-route-analytics/analyze', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  /** SEO Route Page Audit (internal worksheet) */
  static async getSEORoutePageAudit(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    if (params.status) q.set('status', params.status);
    if (params.source_sheet) q.set('source_sheet', params.source_sheet);
    q.set('limit', String(Math.min(Math.max(parseInt(String(params.limit ?? 500), 10) || 500, 1), 2000)));
    if (params.offset != null && params.offset !== '') q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.bffRequest(`/seo-route-page-audit${qs ? `?${qs}` : ''}`);
  }

  static async createSEORoutePageAudit(input) {
    return this.bffRequest('/seo-route-page-audit', { method: 'POST', body: JSON.stringify(input) });
  }

  static async updateSEORoutePageAudit(id, input) {
    return this.bffRequest(`/seo-route-page-audit/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteSEORoutePageAudit(id) {
    return this.bffRequest(`/seo-route-page-audit/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** Import audit rows from frontend-nextjs/Seo/*.csv (keyword files) or raw worksheet CSV. */
  static async importSeoRoutePageAuditCsv({ file, csv, updateExisting = false } = {}) {
    return this.bffRequest('/seo-route-page-audit/import-csv', {
      method: 'POST',
      body: JSON.stringify({
        ...(file ? { file: String(file).trim() } : {}),
        ...(csv ? { csv: String(csv) } : {}),
        updateExisting: updateExisting === true,
      }),
    });
  }

  /** Create audit rows for analytics URLs not yet in the audit table. */
  static async populateAuditFromAnalytics() {
    return this.bffRequest('/seo-route-page-audit/populate-from-analytics', { method: 'POST' });
  }

  static async getSEORouteAuditWorkbookSheets() {
    return this.bffRequest('/seo-route-page-audit/workbook/sheets');
  }

  static async getSEORouteAuditWorkbookSheet(sheetKey, params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    q.set('limit', String(Math.min(Math.max(parseInt(String(params.limit ?? 100), 10) || 100, 1), 500)));
    if (params.offset != null && params.offset !== '') q.set('offset', String(params.offset));
    const filterParams = [
      'page_type',
      'priority',
      'status',
      'content_status',
      'meta_status',
      'thin_content_risk',
      'indexability',
      'category',
      'impact',
      'intent',
    ];
    for (const key of filterParams) {
      if (params[key] != null && String(params[key]).trim() !== '') q.set(key, String(params[key]).trim());
    }
    const qs = q.toString();
    return this.bffRequest(`/seo-route-page-audit/workbook/${encodeURIComponent(sheetKey)}${qs ? `?${qs}` : ''}`);
  }

  static async getSEORouteAuditWorkbookFilterOptions(sheetKey, column) {
    const q = new URLSearchParams({ column: String(column || '') });
    return this.bffRequest(
      `/seo-route-page-audit/workbook/${encodeURIComponent(sheetKey)}/filter-options?${q.toString()}`,
    );
  }

  static async bulkSEORouteAuditWorkbook(sheetKey, body) {
    return this.bffRequest(`/seo-route-page-audit/workbook/${encodeURIComponent(sheetKey)}/bulk`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Import all worksheets from Tejas_Fleets_Prioritized_SEO_Action_Plan.xlsx */
  static async importSeoActionPlanXlsx({ file, xlsxBase64, updateExisting = false } = {}) {
    return this.bffRequest('/seo-route-page-audit/import-xlsx', {
      method: 'POST',
      body: JSON.stringify({
        ...(file ? { file: String(file).trim() } : {}),
        ...(xlsxBase64 ? { xlsxBase64: String(xlsxBase64) } : {}),
        updateExisting: updateExisting === true,
      }),
    });
  }

  static async listSitemaps() {
    return this.bffRequest('/admin/sitemaps');
  }

  static async getSitemapGeneratorCatalog() {
    return this.bffRequest('/admin/sitemaps/generator/catalog');
  }

  static async getCanonicalSitemapFeeds() {
    return this.bffRequest('/admin/sitemaps/canonical-feeds');
  }

  static async listSitemapRegistryPages(categoryKey) {
    const query = categoryKey ? `?categoryKey=${encodeURIComponent(categoryKey)}` : '';
    return this.bffRequest(`/admin/sitemaps/generator/registry-pages${query}`);
  }

  static async validateSitemapRegistryPage(payload) {
    return this.bffRequest('/admin/sitemaps/generator/registry-pages/validate', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  static async createSitemapRegistryPage(payload) {
    return this.bffRequest('/admin/sitemaps/generator/registry-pages', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  static async updateSitemapRegistryPage(id, payload) {
    return this.bffRequest(`/admin/sitemaps/generator/registry-pages/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload || {}),
    });
  }

  static async deleteSitemapRegistryPage(id) {
    return this.bffRequest(`/admin/sitemaps/generator/registry-pages/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async previewSitemapGeneration(selections) {
    return this.bffRequest('/admin/sitemaps/generator/preview', {
      method: 'POST',
      body: JSON.stringify({ selections }),
    });
  }

  static async generateDynamicSitemap({ selections, conflictStrategy, autoRegenerate }) {
    return this.bffRequest('/admin/sitemaps/generator/generate', {
      method: 'POST',
      body: JSON.stringify({ selections, conflictStrategy, autoRegenerate }),
    });
  }

  static async getSitemapHistory() {
    return this.bffRequest('/admin/sitemaps/history');
  }

  static async getSitemapHistoryEntry(id) {
    return this.bffRequest(`/admin/sitemaps/history/${encodeURIComponent(id)}`);
  }

  static async regenerateSitemapHistory(id) {
    return this.bffRequest(`/admin/sitemaps/history/${encodeURIComponent(id)}/regenerate`, { method: 'POST' });
  }

  static async deleteSitemapHistory(id) {
    return this.bffRequest(`/admin/sitemaps/history/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async updateSitemapHistoryStatus(id, status) {
    return this.bffRequest(`/admin/sitemaps/history/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  static async getSitemapConfiguration() {
    return this.bffRequest('/admin/sitemaps/configuration');
  }

  static async updateSitemapConfiguration(configuration) {
    return this.bffRequest('/admin/sitemaps/configuration', {
      method: 'PUT',
      body: JSON.stringify(configuration),
    });
  }

  static async getRobotsTxtConfiguration() {
    return this.bffRequest('/admin/robots-txt/configuration');
  }

  static async getSeoControlCenterSummary() {
    return this.bffRequest('/admin/seo/control-center');
  }

  static async getSeoImageInventory(params = {}) {
    const q = new URLSearchParams();
    if (params.format) q.set('format', params.format);
    const qs = q.toString();
    return this.bffRequest(`/admin/seo/image-inventory${qs ? `?${qs}` : ''}`);
  }

  static async importSeoImageInventoryCsv(csv) {
    return this.bffRequest('/admin/seo/image-inventory/import', {
      method: 'POST',
      body: JSON.stringify({ csv }),
    });
  }

  static async updateSeoImageInventoryRow(id, payload) {
    return this.bffRequest(`/admin/seo/image-inventory/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload || {}),
    });
  }

  static async upsertSeoImageInventoryRow(payload) {
    return this.bffRequest('/admin/seo/image-inventory', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  static async getSeoPageInventory(params = {}) {
    const q = new URLSearchParams();
    if (params.format) q.set('format', params.format);
    if (params.q) q.set('q', params.q);
    if (params.issue && params.issue !== 'all') q.set('issue', params.issue);
    if (params.section && params.section !== 'all') q.set('section', params.section);
    if (params.page) q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    const qs = q.toString();
    return this.bffRequest(`/admin/seo/page-inventory${qs ? `?${qs}` : ''}`);
  }

  static async downloadSeoPageInventoryCsv(params = {}) {
    const q = new URLSearchParams();
    q.set('format', 'csv');
    if (params.q) q.set('q', params.q);
    if (params.issue && params.issue !== 'all') q.set('issue', params.issue);
    if (params.section && params.section !== 'all') q.set('section', params.section);
    const url = `${getBffBaseUrl()}/admin/seo/page-inventory?${q.toString()}`;
    let res;
    try {
      res = await fetch(url, {
        credentials: 'include',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          ...getBffInternalAuthHeaders(),
          ...this.adminBearerHeaders(),
        },
      });
    } catch (err) {
      throw new Error(bffNetworkErrorMessage('/admin/seo/page-inventory', err));
    }
    if (!res.ok) {
      throw new Error('Failed to export page URL CSV');
    }
    return res.text();
  }

  static async importSeoPageInventoryCsv(csv) {
    return this.bffRequest('/admin/seo/page-inventory/import', {
      method: 'POST',
      body: JSON.stringify({ csv }),
    });
  }

  static async updateSeoPageInventoryRow(id, payload) {
    return this.bffRequest(`/admin/seo/page-inventory/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload || {}),
    });
  }

  static async upsertSeoPageInventoryRow(payload) {
    return this.bffRequest('/admin/seo/page-inventory', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  static async getSeoAnalyzer() {
    return this.bffRequest('/admin/seo/analyzer');
  }

  static async getSeo404Monitor() {
    return this.bffRequest('/admin/seo/404-monitor');
  }

  static async getSeoContentOverview() {
    return this.bffRequest('/admin/seo/content-overview');
  }

  static async getSeoRankTrackerKeywords(params = {}) {
    const q = new URLSearchParams();
    if (params.active != null) q.set('active', String(params.active));
    if (params.limit != null) q.set('limit', String(params.limit));
    const qs = q.toString();
    return this.bffRequest(`/admin/seo/rank-tracker${qs ? `?${qs}` : ''}`);
  }

  static async getSeoRankTrackerSummary() {
    return this.bffRequest('/admin/seo/rank-tracker/summary');
  }

  static async createSeoRankKeyword(payload) {
    return this.bffRequest('/admin/seo/rank-tracker', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  static async updateSeoRankKeyword(id, payload) {
    return this.bffRequest(`/admin/seo/rank-tracker/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload || {}),
    });
  }

  static async deleteSeoRankKeyword(id) {
    return this.bffRequest(`/admin/seo/rank-tracker/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async previewRobotsTxt(payload) {
    return this.bffRequest('/admin/robots-txt/preview', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  static async saveRobotsTxtDraft(payload) {
    return this.bffRequest('/admin/robots-txt/draft', {
      method: 'PUT',
      body: JSON.stringify(payload || {}),
    });
  }

  static async publishRobotsTxt(payload) {
    return this.bffRequest('/admin/robots-txt/publish', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  static async getRobotsTxtHistory() {
    return this.bffRequest('/admin/robots-txt/history');
  }

  static async getRobotsTxtRevision(id) {
    return this.bffRequest(`/admin/robots-txt/history/${encodeURIComponent(id)}`);
  }

  static async restoreRobotsTxtRevision(id) {
    return this.bffRequest(`/admin/robots-txt/history/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
    });
  }

  static async compareRobotsTxtRevisions(left, right) {
    const qs = new URLSearchParams({ left, right }).toString();
    return this.bffRequest(`/admin/robots-txt/compare?${qs}`);
  }

  static async getRobotsTxtBlockedUrls() {
    return this.bffRequest('/admin/robots-txt/blocked-urls');
  }

  // --- Snack box ---
  static async getSnackProducts(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/admin/snacks/products${q}`);
  }

  static async createSnackProduct(input) {
    return this.bffRequest('/admin/snacks/products', { method: 'POST', body: JSON.stringify(input) });
  }

  static async updateSnackProduct(id, input) {
    return this.bffRequest(`/admin/snacks/products/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteSnackProduct(id) {
    return this.bffRequest(`/admin/snacks/products/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async uploadSnackProductImage(file, slug) {
    const formData = new FormData();
    formData.append('image', file);
    if (slug) formData.append('slug', slug);
    return this.bffRequest('/admin/snacks/products/upload-image', { method: 'POST', body: formData });
  }

  static async getSnackCategories(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/admin/snacks/categories${q}`);
  }

  static async createSnackCategory(input) {
    return this.bffRequest('/admin/snacks/categories', { method: 'POST', body: JSON.stringify(input) });
  }

  static async updateSnackCategory(id, input) {
    return this.bffRequest(`/admin/snacks/categories/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteSnackCategory(id) {
    return this.bffRequest(`/admin/snacks/categories/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async getSnackBranches(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/admin/snacks/branches${q}`);
  }

  static async createSnackBranch(input) {
    return this.bffRequest('/admin/snacks/branches', { method: 'POST', body: JSON.stringify(input) });
  }

  static async updateSnackBranch(id, input) {
    return this.bffRequest(`/admin/snacks/branches/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteSnackBranch(id) {
    return this.bffRequest(`/admin/snacks/branches/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async getSnackBoxDashboard(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/admin/snacks/dashboard${q}`);
  }

  static async getSnackBoxOrders(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/admin/snacks/orders${q}`);
  }

  static async getSnackBoxReports(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/admin/snacks/reports${q}`);
  }

  static async getSnackBoxSettings(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/admin/snacks/settings${q}`);
  }

  static async saveSnackBoxSettings(input) {
    return this.bffRequest('/admin/snacks/settings', { method: 'PUT', body: JSON.stringify(input) });
  }

  static async exportSnackProducts(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/admin/snacks/products/export${q}`);
  }

  static async importSnackProducts(csv, city) {
    return this.bffRequest('/admin/snacks/products/import', {
      method: 'POST',
      body: JSON.stringify({ csv, city }),
    });
  }

  static async exportSnackCategories() {
    return this.bffRequest('/admin/snacks/categories/export');
  }

  static async importSnackCategories(csv) {
    return this.bffRequest('/admin/snacks/categories/import', {
      method: 'POST',
      body: JSON.stringify({ csv }),
    });
  }

  static async exportSnackOrders(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/admin/snacks/orders/export${q}`);
  }

  static async exportSnackBranches(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/admin/snacks/branches/export${q}`);
  }

  static async importSnackBranches(csv) {
    return this.bffRequest('/admin/snacks/branches/import', {
      method: 'POST',
      body: JSON.stringify({ csv }),
    });
  }

  static async getBookingSnackItems(bookingId) {
    return this.bffRequest(`/admin/snacks/bookings/${encodeURIComponent(bookingId)}/items`);
  }

  static async generateSitemap(id) {
    return this.bffRequest(`/admin/sitemaps/${encodeURIComponent(id)}/generate`, { method: 'POST' });
  }

  static async generateAllSitemaps() {
    return this.bffRequest('/admin/sitemaps/generate-all', { method: 'POST' });
  }

  static async downloadSitemap(id) {
    const url = `${getBffBaseUrl()}/admin/sitemaps/${encodeURIComponent(id)}/download`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: {
        Accept: 'application/xml',
        ...this.adminBearerHeaders(),
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (this.isBffUnauthorized(res.status, body, url)) {
        this.clearAdminSession();
      }
      throw new Error(body.message || body.error || 'Download failed');
    }
    return res.blob();
  }

  /** Generated sitemap XML is served by the BFF public router, not an admin download route. */
  static async downloadGeneratedSitemap(filename) {
    const url = `${getBffBaseUrl()}/public/sitemaps/${encodeURIComponent(filename)}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: {
        Accept: 'application/xml',
        ...this.adminBearerHeaders(),
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (this.isBffUnauthorized(res.status, body, url)) {
        this.clearAdminSession();
      }
      throw new Error(body.message || body.error || 'Download failed');
    }
    return res.blob();
  }

  static async getCmsRoutePages(params = {}) {
    const q = new URLSearchParams();
    if (params.search && String(params.search).trim()) q.set('search', String(params.search).trim());
    if (params.status && String(params.status).trim()) q.set('status', String(params.status).trim());
    q.set('limit', String(Math.min(Math.max(parseInt(String(params.limit ?? 200), 10) || 200, 1), 2000)));
    if (params.offset != null && params.offset !== '') q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.bffRequest(`/cms-route-pages${qs ? `?${qs}` : ''}`);
  }

  static async createCmsRoutePage(input) {
    return this.bffRequest('/cms-route-pages', { method: 'POST', body: JSON.stringify(input) });
  }

  static async updateCmsRoutePage(id, input) {
    return this.bffRequest(`/cms-route-pages/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteCmsRoutePage(id) {
    return this.bffRequest(`/cms-route-pages/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async getCmsRoutePageCss() {
    return this.bffRequest('/cms-route-pages/css');
  }

  static async updateCmsRoutePageCss(css) {
    return this.bffRequest('/cms-route-pages/css', {
      method: 'PUT',
      body: JSON.stringify({ css: String(css || '') }),
    });
  }

  static async getCmsRoutePageCssHistory() {
    return this.bffRequest('/cms-route-pages/css/history');
  }

  static async rollbackCmsRoutePageCss(revisionId) {
    return this.bffRequest('/cms-route-pages/css/rollback', {
      method: 'POST',
      body: JSON.stringify({ revision_id: revisionId }),
    });
  }

  /** Homepage Popular Routes slider (PostgreSQL `homepage_popular_routes`) */
  static async getHomepagePopularRoutesAdmin() {
    return this.bffRequest('/pricing/admin/homepage-popular-routes');
  }

  static async createHomepagePopularRoute(input) {
    return this.bffRequest('/pricing/admin/homepage-popular-routes', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateHomepagePopularRoute(id, input) {
    return this.bffRequest(`/pricing/admin/homepage-popular-routes/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteHomepagePopularRoute(id) {
    return this.bffRequest(`/pricing/admin/homepage-popular-routes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  /** URL rewrites (PostgreSQL `url_rewrites`) */
  static async getUrlRewrites(params = {}) {
    const q = new URLSearchParams();
    const keys = [
      'search',
      'entity_type',
      'redirect_type',
      'is_active',
      'page',
      'limit',
      'sortBy',
      'sortOrder',
    ];
    for (const key of keys) {
      if (params[key] != null && params[key] !== '') q.set(key, String(params[key]));
    }
    const qs = q.toString();
    return this.bffRequest(`/pricing/admin/url-rewrites${qs ? `?${qs}` : ''}`);
  }

  static async getUrlRewrite(id) {
    return this.bffRequest(`/pricing/admin/url-rewrites/${encodeURIComponent(id)}`);
  }

  static async createUrlRewrite(input) {
    return this.bffRequest('/pricing/admin/url-rewrites', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateUrlRewrite(id, input) {
    return this.bffRequest(`/pricing/admin/url-rewrites/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteUrlRewrite(id) {
    return this.bffRequest(`/pricing/admin/url-rewrites/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  /** Homepage sections — JSON `config` per slug (`homepage_sections` table) */
  static async getHomepageSectionsAdmin() {
    return this.bffRequest('/pricing/admin/homepage-sections');
  }

  static async getHomepageSectionAdmin(slug) {
    return this.bffRequest(`/pricing/admin/homepage-sections/${encodeURIComponent(slug)}`);
  }

  static async updateHomepageSectionAdmin(slug, input) {
    return this.bffRequest(`/pricing/admin/homepage-sections/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      body: JSON.stringify(input || {}),
    });
  }

  /** 404 encounter grid — paths users hit that rendered not-found */
  static async getNotFoundEncounters(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== '') qs.set(k, String(v));
    });
    const q = qs.toString();
    return this.bffRequest(`/pricing/admin/not-found-encounters${q ? `?${q}` : ''}`);
  }

  static async deleteNotFoundEncounter(id) {
    return this.bffRequest(`/pricing/admin/not-found-encounters/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async setNotFoundEncounterResolved(id, resolved, note = '') {
    return this.bffRequest(`/pricing/admin/not-found-encounters/${encodeURIComponent(id)}/resolved`, {
      method: 'PATCH',
      body: JSON.stringify({ resolved: Boolean(resolved), note }),
    });
  }

  /**
   * Upload a Popular Routes card image. File lands in
   * `frontend-nextjs/public/images/popular-routes/` and the returned
   * `data.path` is what to store in the row's `image` column.
   */
  static async uploadHomepagePopularRouteImage(file, slug) {
    const formData = new FormData();
    formData.append('image', file);
    if (slug) formData.append('slug', slug);
    return this.bffRequest('/pricing/admin/homepage-popular-routes/upload-image', {
      method: 'POST',
      body: formData,
    });
  }

  /** Homepage Popular Route Categories — filter pills (PostgreSQL `homepage_popular_route_categories`) */
  static async getHomepagePopularRouteCategoriesAdmin() {
    return this.bffRequest('/pricing/admin/homepage-popular-route-categories');
  }

  static async createHomepagePopularRouteCategory(input) {
    return this.bffRequest('/pricing/admin/homepage-popular-route-categories', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateHomepagePopularRouteCategory(id, input) {
    return this.bffRequest(`/pricing/admin/homepage-popular-route-categories/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteHomepagePopularRouteCategory(id) {
    return this.bffRequest(`/pricing/admin/homepage-popular-route-categories/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // --- FAQ management (PostgreSQL `faq_categories`, `faqs`) ---
  static async getFaqCategories(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/faq-categories${q}`);
  }

  static async getFaqCategoryOptions() {
    return this.bffRequest('/pricing/admin/faq-categories/options');
  }

  static async getFaqCategory(id) {
    return this.bffRequest(`/pricing/admin/faq-categories/${encodeURIComponent(id)}`);
  }

  static async createFaqCategory(input) {
    return this.bffRequest('/pricing/admin/faq-categories', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateFaqCategory(id, input) {
    return this.bffRequest(`/pricing/admin/faq-categories/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteFaqCategory(id) {
    return this.bffRequest(`/pricing/admin/faq-categories/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async bulkDeleteFaqCategories(ids) {
    return this.bffRequest('/pricing/admin/faq-categories/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  static async bulkSetFaqCategoryStatus(ids, status) {
    return this.bffRequest('/pricing/admin/faq-categories/bulk-status', {
      method: 'POST',
      body: JSON.stringify({ ids, status }),
    });
  }

  static async getFaqs(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/faqs${q}`);
  }

  static async getFaq(id) {
    return this.bffRequest(`/pricing/admin/faqs/${encodeURIComponent(id)}`);
  }

  static async createFaq(input) {
    return this.bffRequest('/pricing/admin/faqs', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateFaq(id, input) {
    return this.bffRequest(`/pricing/admin/faqs/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteFaq(id) {
    return this.bffRequest(`/pricing/admin/faqs/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async bulkDeleteFaqs(ids) {
    return this.bffRequest('/pricing/admin/faqs/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  static async bulkSetFaqStatus(ids, status) {
    return this.bffRequest('/pricing/admin/faqs/bulk-status', {
      method: 'POST',
      body: JSON.stringify({ ids, status }),
    });
  }

  static async importFaqsCsv(csv, options = {}) {
    return this.bffRequest('/pricing/admin/faqs/import-csv', {
      method: 'POST',
      body: JSON.stringify({
        csv,
        createCategories: options.createCategories !== false,
        updateExisting: options.updateExisting !== false,
      }),
    });
  }

  static async getFaqAuditLogs(params = {}) {
    const q = new URLSearchParams();
    if (params.entity_type) q.set('entity_type', params.entity_type);
    if (params.entity_id != null) q.set('entity_id', String(params.entity_id));
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return this.bffRequest(`/pricing/admin/faq-audit-logs${qs ? `?${qs}` : ''}`);
  }

  // --- Blog management (PostgreSQL `blog_posts`, `blog_categories`, `blog_tags`) ---
  static async getBlogCategories(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/blog-categories${q}`);
  }

  static async getBlogCategoryOptions() {
    return this.bffRequest('/pricing/admin/blog-categories/options');
  }

  static async getBlogAuthors() {
    return this.bffRequest('/pricing/admin/blog-authors');
  }

  static async createBlogAuthor(input) {
    return this.bffRequest('/pricing/admin/blog-authors', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async getBlogSeoDashboard() {
    return this.bffRequest('/pricing/admin/blog-seo/dashboard');
  }

  static async getBlogClusters() {
    return this.bffRequest('/pricing/admin/blog-clusters');
  }

  static async getBlogCluster(id) {
    return this.bffRequest(`/pricing/admin/blog-clusters/${encodeURIComponent(id)}`);
  }

  static async createBlogCluster(input) {
    return this.bffRequest('/pricing/admin/blog-clusters', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateBlogCluster(id, input) {
    return this.bffRequest(`/pricing/admin/blog-clusters/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async getBlogKeywords(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/blog-keywords${q}`);
  }

  static async createBlogKeyword(input) {
    return this.bffRequest('/pricing/admin/blog-keywords', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateBlogKeyword(id, input) {
    return this.bffRequest(`/pricing/admin/blog-keywords/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async getBlogContentCalendar(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/blog-content-calendar${q}`);
  }

  static async createBlogCalendarItem(input) {
    return this.bffRequest('/pricing/admin/blog-content-calendar', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateBlogCalendarItem(id, input) {
    return this.bffRequest(`/pricing/admin/blog-content-calendar/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async getBlogInternalLinks(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/blog-internal-links${q}`);
  }

  static async createBlogInternalLink(input) {
    return this.bffRequest('/pricing/admin/blog-internal-links', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async getBlogRedirects() {
    return this.bffRequest('/pricing/admin/blog-redirects');
  }

  static async createBlogRedirect(input) {
    return this.bffRequest('/pricing/admin/blog-redirects', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async getBlogSeoAudit(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/blog-seo/audit${q}`);
  }

  static async getBlogCannibalization(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/blog-seo/cannibalization${q}`);
  }

  static async generateBlogBrief(input) {
    return this.bffRequest('/pricing/admin/blog-seo/brief', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async generateBlogOutline(input) {
    return this.bffRequest('/pricing/admin/blog-seo/outline', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async previewBlogPublish(id, input = {}) {
    return this.bffRequest(`/pricing/admin/blog-posts/${encodeURIComponent(id)}/publish-preview`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async getBlogCategory(id) {
    return this.bffRequest(`/pricing/admin/blog-categories/${encodeURIComponent(id)}`);
  }

  static async createBlogCategory(input) {
    return this.bffRequest('/pricing/admin/blog-categories', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateBlogCategory(id, input) {
    return this.bffRequest(`/pricing/admin/blog-categories/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteBlogCategory(id) {
    return this.bffRequest(`/pricing/admin/blog-categories/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async getBlogPosts(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/blog-posts${q}`);
  }

  static async getBlogPost(id) {
    return this.bffRequest(`/pricing/admin/blog-posts/${encodeURIComponent(id)}`);
  }

  static async createBlogPost(input) {
    return this.bffRequest('/pricing/admin/blog-posts', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateBlogPost(id, input) {
    return this.bffRequest(`/pricing/admin/blog-posts/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteBlogPost(id) {
    return this.bffRequest(`/pricing/admin/blog-posts/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /**
   * Upload a blog post featured image. File lands in
   * `frontend-nextjs/public/images/blog/` and the returned
   * `data.path` is what to store in `featured_image_url`.
   */
  static async uploadBlogPostImage(file, slug) {
    const formData = new FormData();
    formData.append('image', file);
    if (slug) formData.append('slug', slug);
    return this.bffRequest('/pricing/admin/blog-posts/upload-image', {
      method: 'POST',
      body: formData,
    });
  }

  static async getBlogTags(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/blog-tags${q}`);
  }

  static async getBlogTagOptions() {
    return this.bffRequest('/pricing/admin/blog-tags/options');
  }

  static async getBlogTag(id) {
    return this.bffRequest(`/pricing/admin/blog-tags/${encodeURIComponent(id)}`);
  }

  static async createBlogTag(input) {
    return this.bffRequest('/pricing/admin/blog-tags', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateBlogTag(id, input) {
    return this.bffRequest(`/pricing/admin/blog-tags/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteBlogTag(id) {
    return this.bffRequest(`/pricing/admin/blog-tags/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async getBlogComments(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/blog-comments${q}`);
  }

  static async getBlogCommentStats() {
    return this.bffRequest('/pricing/admin/blog-comments/stats');
  }

  static async getBlogCommentPostOptions() {
    return this.bffRequest('/pricing/admin/blog-comments/post-options');
  }

  static async getBlogComment(id) {
    return this.bffRequest(`/pricing/admin/blog-comments/${encodeURIComponent(id)}`);
  }

  static async updateBlogComment(id, input) {
    return this.bffRequest(`/pricing/admin/blog-comments/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteBlogComment(id) {
    return this.bffRequest(`/pricing/admin/blog-comments/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async bulkSetBlogCommentStatus(ids, status) {
    return this.bffRequest('/pricing/admin/blog-comments/bulk-status', {
      method: 'POST',
      body: JSON.stringify({ ids, status }),
    });
  }

  static async bulkDeleteBlogComments(ids) {
    return this.bffRequest('/pricing/admin/blog-comments/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  // --- Checkout add-ons (booking confirmation) ---
  static async getCheckoutAddons(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/checkout-addons${q}`);
  }

  static async getCheckoutAddon(id) {
    return this.bffRequest(`/pricing/admin/checkout-addons/${encodeURIComponent(id)}`);
  }

  static async createCheckoutAddon(input) {
    return this.bffRequest('/pricing/admin/checkout-addons', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateCheckoutAddon(id, input) {
    return this.bffRequest(`/pricing/admin/checkout-addons/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteCheckoutAddon(id) {
    return this.bffRequest(`/pricing/admin/checkout-addons/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async importSharedCheckoutAddons(options = {}) {
    return this.bffRequest('/pricing/admin/checkout-addons/import-shared', {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  static async getCheckoutAddonOrders(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/checkout-addons/orders${q}`);
  }

  static async exportCheckoutAddonOrders(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/checkout-addons/orders/export${q}`);
  }

  static async getCheckoutOnlinePaymentOptions(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/checkout-online-payment-options${q}`);
  }

  static async getCheckoutOnlinePaymentOption(id) {
    return this.bffRequest(`/pricing/admin/checkout-online-payment-options/${encodeURIComponent(id)}`);
  }

  static async createCheckoutOnlinePaymentOption(input) {
    return this.bffRequest('/pricing/admin/checkout-online-payment-options', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateCheckoutOnlinePaymentOption(id, input) {
    return this.bffRequest(`/pricing/admin/checkout-online-payment-options/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteCheckoutOnlinePaymentOption(id) {
    return this.bffRequest(`/pricing/admin/checkout-online-payment-options/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async importSharedCheckoutOnlinePaymentOptions(options = {}) {
    return this.bffRequest('/pricing/admin/checkout-online-payment-options/import-shared', {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  static async getCheckoutUrgentPaymentPolicy() {
    return this.bffRequest('/pricing/admin/checkout-urgent-payment-policy');
  }

  static async saveCheckoutUrgentPaymentPolicy(config) {
    return this.bffRequest('/pricing/admin/checkout-urgent-payment-policy', {
      method: 'PUT',
      body: JSON.stringify({ config }),
    });
  }

  static async getOnewayMultistopPolicy() {
    return this.bffRequest('/pricing/admin/oneway-multistop-policy');
  }

  static async saveOnewayMultistopPolicy(config) {
    return this.bffRequest('/pricing/admin/oneway-multistop-policy', {
      method: 'PUT',
      body: JSON.stringify({ config }),
    });
  }

  static async getOnewayBillableKmPolicy() {
    return this.bffRequest('/pricing/admin/oneway-billable-km-policy');
  }

  static async saveOnewayBillableKmPolicy(config) {
    return this.bffRequest('/pricing/admin/oneway-billable-km-policy', {
      method: 'PUT',
      body: JSON.stringify({ config }),
    });
  }

  static async getPeakPricingPolicy() {
    return this.bffRequest('/pricing/admin/peak-pricing-policy');
  }

  static async savePeakPricingPolicy(config) {
    return this.bffRequest('/pricing/admin/peak-pricing-policy', {
      method: 'PUT',
      body: JSON.stringify({ config }),
    });
  }

  static async getCheckoutTermsConditions(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/checkout-terms-conditions${q}`);
  }

  static async getCheckoutTermsCondition(id) {
    return this.bffRequest(`/pricing/admin/checkout-terms-conditions/${encodeURIComponent(id)}`);
  }

  static async createCheckoutTermsCondition(input) {
    return this.bffRequest('/pricing/admin/checkout-terms-conditions', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateCheckoutTermsCondition(id, input) {
    return this.bffRequest(`/pricing/admin/checkout-terms-conditions/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteCheckoutTermsCondition(id) {
    return this.bffRequest(`/pricing/admin/checkout-terms-conditions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  /** Bulk upsert vehicle pricing rows (Outstation / Airport / Special packages). */
  static async importVehiclePricingCsv(csvText, category = 'outstation') {
    const q = new URLSearchParams({ category: String(category || 'outstation') });
    return this.bffRequest(`/pricing/admin/vehicle-pricing/import?${q.toString()}`, {
      method: 'POST',
      body: JSON.stringify({ csv: csvText }),
    });
  }

  /** Bulk upsert from city-wise outstation JSON (`records[]` with city, cab_type, per_km, …). */
  static async importVehiclePricingJson(payload, category = 'outstation') {
    const q = new URLSearchParams({ category: String(category || 'outstation') });
    return this.bffRequest(`/pricing/admin/vehicle-pricing/import?${q.toString()}`, {
      method: 'POST',
      body: JSON.stringify({ json: payload }),
    });
  }

  /** Vendor cost settlement engine (vehicle categories + vendor_route_pricing). */
  static async getVehicleCategories() {
    return this.bffRequest('/pricing/admin/vehicle-categories');
  }

  static async getVendorRoutePricing(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/vendor-route-pricing${q}`);
  }

  static async createVendorRoutePricing(input) {
    return this.bffRequest('/pricing/admin/vendor-route-pricing', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateVendorRoutePricing(id, input) {
    return this.bffRequest(`/pricing/admin/vendor-route-pricing/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteVendorRoutePricing(id) {
    return this.bffRequest(`/pricing/admin/vendor-route-pricing/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async previewVendorFare(input) {
    return this.bffRequest('/pricing/admin/vendor-fare-preview', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /** Quote-only pricing test matrix (no bookings). */
  static async getPricingTestVehicleTypes() {
    return this.bffRequest('/pricing/admin/pricing-test/vehicle-types');
  }

  static async runPricingTest(input) {
    return this.bffRequest('/pricing/admin/pricing-test/run', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /** Competitor price analysis (Savaari + future sources). */
  static async getPriceAnalysisSummary(analysisRunId) {
    const q = analysisRunId ? `?analysisRunId=${encodeURIComponent(analysisRunId)}` : '';
    return this.bffRequest(`/pricing/admin/competitor-analysis/summary${q}`);
  }

  static async getPriceAnalysisGrid(tripType, query = {}) {
    const q = new URLSearchParams(query).toString();
    return this.bffRequest(`/pricing/admin/competitor-analysis/${tripType}${q ? `?${q}` : ''}`);
  }

  static async startPriceAnalysis(config) {
    return this.bffRequest('/pricing/admin/competitor-analysis/start', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  static async getPriceAnalysisRuns(query = {}) {
    const q = new URLSearchParams(query).toString();
    return this.bffRequest(`/pricing/admin/competitor-analysis/runs${q ? `?${q}` : ''}`);
  }

  static async getPriceAnalysisRun(id) {
    return this.bffRequest(`/pricing/admin/competitor-analysis/runs/${encodeURIComponent(id)}`);
  }

  static async cancelPriceAnalysisRun(id) {
    return this.bffRequest(`/pricing/admin/competitor-analysis/runs/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
    });
  }

  static async retryPriceAnalysisRun(id) {
    return this.bffRequest(`/pricing/admin/competitor-analysis/runs/${encodeURIComponent(id)}/retry`, {
      method: 'POST',
    });
  }

  static async exportPriceAnalysis(tripType, query = {}, format = 'csv') {
    const params = new URLSearchParams({ ...query, tripType, format });
    return this.bffRequest(`/pricing/admin/competitor-analysis/export?${params.toString()}`, {
      raw: true,
    });
  }

  static async getPriceAnalysisCities() {
    return this.bffRequest('/pricing/admin/competitor-analysis/masters/cities');
  }

  static async getPriceAnalysisDestinations() {
    return this.bffRequest('/pricing/admin/competitor-analysis/masters/destinations');
  }

  static async updatePriceAnalysisCitySourceMapping(id, mapping, competitor = 'savaari') {
    return this.bffRequest(`/pricing/admin/competitor-analysis/masters/cities/${encodeURIComponent(id)}/source-mapping`, {
      method: 'PATCH',
      body: JSON.stringify({ competitor, mapping }),
    });
  }

  static async updatePriceAnalysisDestinationSourceMapping(id, mapping, competitor = 'savaari') {
    return this.bffRequest(`/pricing/admin/competitor-analysis/masters/destinations/${encodeURIComponent(id)}/source-mapping`, {
      method: 'PATCH',
      body: JSON.stringify({ competitor, mapping }),
    });
  }

  static async updatePriceAnalysisAirportSourceMapping(id, mapping, competitor = 'savaari') {
    return this.bffRequest(`/pricing/admin/competitor-analysis/masters/airports/${encodeURIComponent(id)}/source-mapping`, {
      method: 'PATCH',
      body: JSON.stringify({ competitor, mapping }),
    });
  }

  static async updatePriceAnalysisLocalPackageSourceMapping(id, mapping, competitor = 'savaari') {
    return this.bffRequest(`/pricing/admin/competitor-analysis/masters/local-packages/${encodeURIComponent(id)}/source-mapping`, {
      method: 'PATCH',
      body: JSON.stringify({ competitor, mapping }),
    });
  }

  static async getPriceAnalysisLocalPackages() {
    return this.bffRequest('/pricing/admin/competitor-analysis/masters/local-packages');
  }

  static async getPriceAnalysisAirports() {
    return this.bffRequest('/pricing/admin/competitor-analysis/masters/airports');
  }

  static async getPriceAnalysisCompetitors() {
    return this.bffRequest('/pricing/admin/competitor-analysis/settings/competitors');
  }

  static async getPriceAnalysisRecord(id) {
    return this.bffRequest(`/pricing/admin/competitor-analysis/records/${encodeURIComponent(id)}`);
  }

  static async comparePriceAnalysisRecords(ids) {
    return this.bffRequest('/pricing/admin/competitor-analysis/compare', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  static async createPriceAnalysisCity(body) {
    return this.bffRequest('/pricing/admin/competitor-analysis/masters/cities', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async createPriceAnalysisRoute(body) {
    return this.bffRequest('/pricing/admin/competitor-analysis/masters/routes', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async getPriceAnalysisRoutes() {
    return this.bffRequest('/pricing/admin/competitor-analysis/masters/routes');
  }

  static async getPriceAnalysisVehicleMappings(competitorId) {
    const q = competitorId ? `?competitorId=${encodeURIComponent(competitorId)}` : '';
    return this.bffRequest(`/pricing/admin/competitor-analysis/masters/vehicle-mappings${q}`);
  }

  static async getPriceAnalysisTrends(query = {}) {
    const q = new URLSearchParams(query).toString();
    return this.bffRequest(`/pricing/admin/competitor-analysis/trends${q ? `?${q}` : ''}`);
  }

  static async getPriceAnalysisAlerts() {
    return this.bffRequest('/pricing/admin/competitor-analysis/alerts');
  }

  static async createPriceAnalysisAlert(body) {
    return this.bffRequest('/pricing/admin/competitor-analysis/alerts', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updatePriceAnalysisAlert(id, body) {
    return this.bffRequest(`/pricing/admin/competitor-analysis/alerts/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async deletePriceAnalysisAlert(id) {
    return this.bffRequest(`/pricing/admin/competitor-analysis/alerts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async getReviews(queryString = '') {
    const q = queryString && !queryString.startsWith('?') ? `?${queryString}` : queryString;
    return this.bffRequest(`/pricing/admin/reviews${q}`);
  }

  static async getReviewPageOptions() {
    return this.bffRequest('/pricing/admin/reviews/page-options');
  }

  static async getReview(id) {
    return this.bffRequest(`/pricing/admin/reviews/${encodeURIComponent(id)}`);
  }

  static async createReview(input) {
    return this.bffRequest('/pricing/admin/reviews', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  static async updateReview(id, input) {
    return this.bffRequest(`/pricing/admin/reviews/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  static async deleteReview(id) {
    return this.bffRequest(`/pricing/admin/reviews/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async bulkDeleteReviews(ids) {
    return this.bffRequest('/pricing/admin/reviews/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  static async bulkSetReviewStatus(ids, status) {
    return this.bffRequest('/pricing/admin/reviews/bulk-status', {
      method: 'POST',
      body: JSON.stringify({ ids, status }),
    });
  }

  static async bulkSetReviewPage(ids, page_to_show) {
    return this.bffRequest('/pricing/admin/reviews/bulk-page', {
      method: 'POST',
      body: JSON.stringify({ ids, page_to_show }),
    });
  }

  static async importReviews({ csv, json, defaultPage, defaultStatus, updateExisting = true } = {}) {
    const body = {
      default_page: defaultPage || undefined,
      default_status: defaultStatus || undefined,
      updateExisting: updateExisting !== false,
    };
    if (json != null && json !== '') body.json = json;
    else body.csv = csv;
    return this.bffRequest('/pricing/admin/reviews/import', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async getUsers(filters = {}) {
    const query = `
      query GetUsers($role: String, $limit: Int, $offset: Int) {
        users(role: $role, limit: $limit, offset: $offset) {
          id
          name
          email
          phone
          role
          status
          email_verified_at
          created_at
          updated_at
        }
      }
    `;

    return this.graphqlRequest(query, filters);
  }

  static async createUser(userData) {
    const mutation = `
      mutation CreateUser($input: UserInput!) {
        createUser(input: $input) {
          id
          name
          email
          phone
          role
          status
          created_at
        }
      }
    `;

    return this.graphqlRequest(mutation, { input: userData });
  }

  static async updateUser(id, userData) {
    const mutation = `
      mutation UpdateUser($id: Int!, $input: UserUpdateInput!) {
        updateUser(id: $id, input: $input) {
          id
          name
          email
          phone
          role
          status
          updated_at
        }
      }
    `;

    return this.graphqlRequest(mutation, { id, input: userData });
  }

  static async deleteUser(id) {
    const mutation = `
      mutation DeleteUser($id: Int!) {
        deleteUser(id: $id)
      }
    `;

    return this.graphqlRequest(mutation, { id });
  }

  static async getReviewAutomationDashboard(queryString = '') {
    const q = String(queryString || '').startsWith('?') ? queryString : queryString ? `?${queryString}` : '';
    return this.bffRequest(`/admin/reviews/dashboard${q}`);
  }

  static async getReviewAutomationReports(queryString = '') {
    const q = String(queryString || '').startsWith('?') ? queryString : queryString ? `?${queryString}` : '';
    return this.bffRequest(`/admin/reviews/reports${q}`);
  }

  static async getReviewAutomationSettings() {
    return this.bffRequest('/admin/reviews/settings');
  }

  static async saveReviewAutomationSettings(input) {
    return this.bffRequest('/admin/reviews/settings', {
      method: 'PUT',
      body: JSON.stringify(input || {}),
    });
  }

  static async getReviewAutomationRequests(queryString = '') {
    const q = String(queryString || '').startsWith('?') ? queryString : queryString ? `?${queryString}` : '';
    return this.bffRequest(`/admin/reviews/requests${q}`);
  }

  static async getReviewAutomationRequest(id) {
    return this.bffRequest(`/admin/reviews/requests/${encodeURIComponent(id)}`);
  }

  static async resendReviewAutomationRequest(id) {
    return this.bffRequest(`/admin/reviews/requests/${encodeURIComponent(id)}/resend`, {
      method: 'POST',
      body: '{}',
    });
  }

  static async retryReviewAutomationRequest(id) {
    return this.bffRequest(`/admin/reviews/requests/${encodeURIComponent(id)}/retry`, {
      method: 'POST',
      body: '{}',
    });
  }

  static async optOutReviewAutomationRequest(id) {
    return this.bffRequest(`/admin/reviews/requests/${encodeURIComponent(id)}/opt-out`, {
      method: 'POST',
      body: '{}',
    });
  }

  static async convertReviewAutomationTestimonial(id) {
    return this.bffRequest(`/admin/reviews/requests/${encodeURIComponent(id)}/testimonial`, {
      method: 'POST',
      body: '{}',
    });
  }

  static async linkReviewAutomationComplaint(id, complaintId) {
    return this.bffRequest(`/admin/reviews/requests/${encodeURIComponent(id)}/link-complaint`, {
      method: 'POST',
      body: JSON.stringify({ complaintId }),
    });
  }

  static async sendReviewAutomationTest(input) {
    return this.bffRequest('/admin/reviews/test-send', {
      method: 'POST',
      body: JSON.stringify(input || {}),
    });
  }

  static async exportReviewAutomationRequests(queryString = '') {
    const q = String(queryString || '').startsWith('?') ? queryString : queryString ? `?${queryString}` : '';
    const url = `${getBffBaseUrl()}/admin/reviews/requests/export${q}`;
    const csrfToken = await ensureCsrfToken();
    const res = await fetch(url, {
      credentials: 'include',
      headers: {
        Accept: 'text/csv',
        ...this.adminBearerHeaders(),
        ...(csrfToken ? { [CSRF_HEADER]: csrfToken } : {}),
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (this.isBffUnauthorized(res.status, body, url)) {
        this.clearAdminSession();
      }
      throw new Error(body.message || 'Export failed');
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = 'review-requests.csv';
    a.click();
    URL.revokeObjectURL(objectUrl);
    return { success: true };
  }

  // Luxury Cars
  static async getLuxuryCarsDashboard() {
    return this.bffRequest('/luxury-cars/admin/dashboard');
  }

  static async getLuxuryCarsSettings() {
    return this.bffRequest('/luxury-cars/admin/settings');
  }

  static async updateLuxuryCarsSettings(key, value) {
    return this.bffRequest(`/luxury-cars/admin/settings/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  static async getLuxuryWelcomeGoodiesSettings() {
    return this.bffRequest('/luxury-cars/admin/welcome-goodies/settings');
  }

  static async updateLuxuryWelcomeGoodiesSettings(body) {
    return this.bffRequest('/luxury-cars/admin/welcome-goodies/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async getLuxuryWelcomeGoodieItems(params = {}) {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    return this.bffRequest(`/luxury-cars/admin/welcome-goodies/items${q.toString() ? `?${q}` : ''}`);
  }

  static async createLuxuryWelcomeGoodieItem(body) {
    return this.bffRequest('/luxury-cars/admin/welcome-goodies/items', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateLuxuryWelcomeGoodieItem(id, body) {
    return this.bffRequest(`/luxury-cars/admin/welcome-goodies/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async deleteLuxuryWelcomeGoodieItem(id) {
    return this.bffRequest(`/luxury-cars/admin/welcome-goodies/items/${id}`, { method: 'DELETE' });
  }

  static async getLuxuryWelcomeGoodiePackages(params = {}) {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    return this.bffRequest(`/luxury-cars/admin/welcome-goodies/packages${q.toString() ? `?${q}` : ''}`);
  }

  static async createLuxuryWelcomeGoodiePackage(body) {
    return this.bffRequest('/luxury-cars/admin/welcome-goodies/packages', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateLuxuryWelcomeGoodiePackage(id, body) {
    return this.bffRequest(`/luxury-cars/admin/welcome-goodies/packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async deleteLuxuryWelcomeGoodiePackage(id) {
    return this.bffRequest(`/luxury-cars/admin/welcome-goodies/packages/${id}`, { method: 'DELETE' });
  }

  static async getLuxuryWelcomeGoodieOrders(params = {}) {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.offset != null) q.set('offset', String(params.offset));
    return this.bffRequest(`/luxury-cars/admin/welcome-goodies/orders${q.toString() ? `?${q}` : ''}`);
  }

  static async getLuxuryWelcomeGoodiesAnalytics() {
    return this.bffRequest('/luxury-cars/admin/welcome-goodies/analytics');
  }

  static async getLuxuryTermsAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/luxury-cars/admin/terms${qs ? `?${qs}` : ''}`);
  }

  static async getLuxuryTermsMeta() {
    return this.bffRequest('/luxury-cars/admin/terms/meta');
  }

  static async getLuxuryTermAdmin(id) {
    return this.bffRequest(`/luxury-cars/admin/terms/${encodeURIComponent(id)}`);
  }

  static async createLuxuryTerm(body) {
    return this.bffRequest('/luxury-cars/admin/terms', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateLuxuryTerm(id, body) {
    return this.bffRequest(`/luxury-cars/admin/terms/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async setLuxuryTermStatus(id, status) {
    return this.bffRequest(`/luxury-cars/admin/terms/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  static async archiveLuxuryTerm(id) {
    return this.bffRequest(`/luxury-cars/admin/terms/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async getLuxuryFaqsAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/luxury-cars/admin/faqs${qs ? `?${qs}` : ''}`);
  }

  static async getLuxuryFaqMeta() {
    return this.bffRequest('/luxury-cars/admin/faqs/meta');
  }

  static async getLuxuryFaqAdmin(id) {
    return this.bffRequest(`/luxury-cars/admin/faqs/${encodeURIComponent(id)}`);
  }

  static async createLuxuryFaq(body) {
    return this.bffRequest('/luxury-cars/admin/faqs', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateLuxuryFaq(id, body) {
    return this.bffRequest(`/luxury-cars/admin/faqs/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async deleteLuxuryFaq(id) {
    return this.bffRequest(`/luxury-cars/admin/faqs/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async publishLuxuryFaq(id) {
    return this.bffRequest(`/luxury-cars/admin/faqs/${encodeURIComponent(id)}/publish`, { method: 'POST' });
  }

  static async unpublishLuxuryFaq(id) {
    return this.bffRequest(`/luxury-cars/admin/faqs/${encodeURIComponent(id)}/unpublish`, { method: 'POST' });
  }

  static async duplicateLuxuryFaq(id) {
    return this.bffRequest(`/luxury-cars/admin/faqs/${encodeURIComponent(id)}/duplicate`, { method: 'POST' });
  }

  static async importLuxuryFaqs(file, sheet = 'Master FAQ') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sheet', sheet);
    return this.bffRequest('/luxury-cars/admin/faqs/import', { method: 'POST', body: formData });
  }

  static async exportLuxuryFaqsCsv(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    const url = `${getBffBaseUrl()}/luxury-cars/admin/faqs/export/csv${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'text/csv', ...this.adminBearerHeaders() },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Export failed');
    }
    return res.blob();
  }

  static async getLuxuryFaqCategories(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/luxury-cars/admin/faq-categories${qs ? `?${qs}` : ''}`);
  }

  static async getLuxuryFaqCategoriesMeta() {
    return this.bffRequest('/luxury-cars/admin/faq-categories/meta');
  }

  static async exportLuxuryFaqCategoriesCsv(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    const url = `${getBffBaseUrl()}/luxury-cars/admin/faq-categories/export/csv${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'text/csv', ...this.adminBearerHeaders() },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Export failed');
    }
    return res.blob();
  }

  static async createLuxuryFaqCategory(body) {
    return this.bffRequest('/luxury-cars/admin/faq-categories', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateLuxuryFaqCategory(id, body) {
    return this.bffRequest(`/luxury-cars/admin/faq-categories/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  static async deleteLuxuryFaqCategory(id) {
    return this.bffRequest(`/luxury-cars/admin/faq-categories/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async getLuxuryFaqTemplates(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/luxury-cars/admin/faqs/templates${qs ? `?${qs}` : ''}`);
  }

  static async getLuxuryFaqTemplatesMeta() {
    return this.bffRequest('/luxury-cars/admin/faqs/templates/meta');
  }

  static async exportLuxuryFaqTemplatesCsv(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    const url = `${getBffBaseUrl()}/luxury-cars/admin/faqs/templates/export/csv${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'text/csv', ...this.adminBearerHeaders() },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Export failed');
    }
    return res.blob();
  }

  static async getLuxuryFaqAnalytics() {
    return this.bffRequest('/luxury-cars/admin/faqs/analytics');
  }

  static async getLuxuryCarsCatalog(type) {
    return this.bffRequest(`/luxury-cars/admin/catalog/${encodeURIComponent(type)}`);
  }

  static async getLuxuryCitiesAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/luxury-cars/admin/cities${qs ? `?${qs}` : ''}`);
  }

  static async getLuxuryCityAdmin(id) {
    return this.bffRequest(`/luxury-cars/admin/cities/${id}`);
  }

  static async createLuxuryCity(body) {
    return this.bffRequest('/luxury-cars/admin/cities', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateLuxuryCity(id, body) {
    return this.bffRequest(`/luxury-cars/admin/cities/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  static async deleteLuxuryCity(id) {
    return this.bffRequest(`/luxury-cars/admin/cities/${id}`, { method: 'DELETE' });
  }

  static async getLuxuryCarsActivities(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/luxury-cars/admin/activities${qs ? `?${qs}` : ''}`);
  }

  static async uploadLuxuryCarImage(file) {
    const formData = new FormData();
    formData.append('image', file);
    return this.bffRequest('/luxury-cars/admin/upload-image', { method: 'POST', body: formData });
  }

  static async getLuxuryCarsAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/luxury-cars/admin/cars${qs ? `?${qs}` : ''}`);
  }

  static async getLuxuryCarAdmin(id) {
    return this.bffRequest(`/luxury-cars/admin/cars/${id}`);
  }

  static async createLuxuryCar(body) {
    return this.bffRequest('/luxury-cars/admin/cars', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateLuxuryCar(id, body) {
    return this.bffRequest(`/luxury-cars/admin/cars/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  static async deleteLuxuryCar(id) {
    return this.bffRequest(`/luxury-cars/admin/cars/${id}`, { method: 'DELETE' });
  }

  static async getLuxuryCatalogAdmin(type) {
    return this.bffRequest(`/luxury-cars/admin/catalog/${encodeURIComponent(type)}/all`);
  }

  static async saveLuxuryCatalogItem(type, body) {
    return this.bffRequest(`/luxury-cars/admin/catalog/${encodeURIComponent(type)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async getLuxuryCityCars(cityId) {
    return this.bffRequest(`/luxury-cars/admin/cities/${cityId}/cars`);
  }

  static async assignLuxuryCityCar(cityId, body) {
    return this.bffRequest(`/luxury-cars/admin/cities/${cityId}/cars`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateLuxuryCityCarMapping(mappingId, body) {
    return this.bffRequest(`/luxury-cars/admin/city-cars/${mappingId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  static async removeLuxuryCityCarMapping(mappingId) {
    return this.bffRequest(`/luxury-cars/admin/city-cars/${mappingId}`, { method: 'DELETE' });
  }

  static async getLuxuryTariffsAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/luxury-cars/admin/tariffs${qs ? `?${qs}` : ''}`);
  }

  static async createLuxuryTariff(body) {
    return this.bffRequest('/luxury-cars/admin/tariffs', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateLuxuryTariff(id, body) {
    return this.bffRequest(`/luxury-cars/admin/tariffs/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  static async getLuxuryPackagesAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    return this.bffRequest(`/luxury-cars/admin/packages${q.toString() ? `?${q}` : ''}`);
  }

  static async createLuxuryPackage(body) {
    return this.bffRequest('/luxury-cars/admin/packages', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateLuxuryPackage(id, body) {
    return this.bffRequest(`/luxury-cars/admin/packages/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  static async getLuxuryPagesAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    return this.bffRequest(`/luxury-cars/admin/pages${q.toString() ? `?${q}` : ''}`);
  }

  static async getLuxuryPageAdmin(id) {
    return this.bffRequest(`/luxury-cars/admin/pages/${id}`);
  }

  static async createLuxuryPage(body) {
    return this.bffRequest('/luxury-cars/admin/pages', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateLuxuryPage(id, body) {
    return this.bffRequest(`/luxury-cars/admin/pages/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  static async publishLuxuryPage(id) {
    return this.bffRequest(`/luxury-cars/admin/pages/${id}/publish`, { method: 'POST' });
  }

  static async createLuxuryPagePreviewToken(id) {
    return this.bffRequest(`/luxury-cars/admin/pages/${id}/preview-token`, { method: 'POST' });
  }

  static async suggestLuxuryPageLinks(id) {
    return this.bffRequest(`/luxury-cars/admin/pages/${id}/suggest-links`);
  }

  static async getLuxuryEnquiriesAdmin(params = {}) {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.city_id) q.set('city_id', params.city_id);
    if (params.search) q.set('search', params.search);
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.offset != null) q.set('offset', String(params.offset));
    return this.bffRequest(`/luxury-cars/admin/enquiries${q.toString() ? `?${q}` : ''}`);
  }

  static async getLuxuryEnquiryAdmin(id) {
    return this.bffRequest(`/luxury-cars/admin/enquiries/${id}`);
  }

  static async updateLuxuryEnquiry(id, body) {
    return this.bffRequest(`/luxury-cars/admin/enquiries/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
  }

  static async getLuxuryEnquiryComments(id) {
    return this.bffRequest(`/luxury-cars/admin/enquiries/${id}/comments`);
  }

  static async addLuxuryEnquiryComment(id, body) {
    return this.bffRequest(`/luxury-cars/admin/enquiries/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async sendLuxuryEnquiryCustomerEmail(id, body) {
    return this.bffRequest(`/luxury-cars/admin/enquiries/${id}/send-email`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async convertLuxuryEnquiryToBooking(id, body = {}) {
    return this.bffRequest(`/luxury-cars/admin/enquiries/${id}/convert-to-booking`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async getLuxuryBookingsAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    return this.bffRequest(`/luxury-cars/admin/bookings${q.toString() ? `?${q}` : ''}`);
  }

  static async getLuxuryBookingAdmin(id) {
    return this.bffRequest(`/luxury-cars/admin/bookings/${id}`);
  }

  static async createLuxuryBooking(body) {
    return this.bffRequest('/luxury-cars/admin/bookings', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateLuxuryBooking(id, body) {
    return this.bffRequest(`/luxury-cars/admin/bookings/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
  }

  static async getLuxuryBookingSupplierOptions(id) {
    return this.bffRequest(`/luxury-cars/admin/bookings/${id}/supplier-options`);
  }

  static async autoAssignLuxuryBooking(id, body = {}) {
    return this.bffRequest(`/luxury-cars/admin/bookings/${id}/auto-assign`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async assignLuxuryBookingVendor(id, body) {
    return this.bffRequest(`/luxury-cars/admin/bookings/${id}/assign-vendor`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async notifyLuxuryBookingCustomer(id) {
    return this.bffRequest(`/luxury-cars/admin/bookings/${id}/notify-customer`, { method: 'POST', body: '{}' });
  }

  static async sendLuxuryBookingAssignmentEmail(id, body = {}) {
    return this.bffRequest(`/luxury-cars/admin/bookings/${id}/send-assignment-email`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async notifyLuxuryBookingVendor(id) {
    return this.bffRequest(`/luxury-cars/admin/bookings/${id}/notify-vendor`, { method: 'POST', body: '{}' });
  }

  static async setLuxuryVendorPortalAccess(id, body) {
    return this.bffRequest(`/luxury-cars/admin/vendors/${id}/portal-access`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async luxuryVendorSupplierLoginAs(id) {
    return this.bffRequest(`/luxury-cars/admin/vendors/${id}/supplier-login-as`, { method: 'POST', body: '{}' });
  }

  static async getLuxuryVendorsAdmin(params = {}) {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.city_id) q.set('city_id', params.city_id);
    if (params.search) q.set('search', params.search);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.offset != null) q.set('offset', String(params.offset));
    return this.bffRequest(`/luxury-cars/admin/vendors${q.toString() ? `?${q}` : ''}`);
  }

  static async getLuxuryVendorAdmin(id) {
    return this.bffRequest(`/luxury-cars/admin/vendors/${id}`);
  }

  static async createLuxuryVendor(body) {
    return this.bffRequest('/luxury-cars/admin/vendors', { method: 'POST', body: JSON.stringify(body) });
  }

  static async updateLuxuryVendor(id, body) {
    return this.bffRequest(`/luxury-cars/admin/vendors/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  static async deleteLuxuryVendor(id) {
    return this.bffRequest(`/luxury-cars/admin/vendors/${id}`, { method: 'DELETE' });
  }

  static async getLuxuryPricingMatrix(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/luxury-cars/admin/pricing/matrix${qs ? `?${qs}` : ''}`);
  }

  static async getLuxuryPricingGaps(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/luxury-cars/admin/pricing/gaps${qs ? `?${qs}` : ''}`);
  }

  static async getLuxuryOperationalAlerts() {
    return this.bffRequest('/luxury-cars/admin/operational-alerts');
  }

  static async getLuxuryPricingRules(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/luxury-cars/admin/pricing/rules${qs ? `?${qs}` : ''}`);
  }

  static async saveLuxuryPricingRule(body) {
    if (body.id) {
      return this.bffRequest(`/luxury-cars/admin/pricing/rules/${body.id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    }
    return this.bffRequest('/luxury-cars/admin/pricing/rules', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async getLuxuryVendorVehiclesAdmin(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return this.bffRequest(`/luxury-cars/admin/vendor-vehicles${qs ? `?${qs}` : ''}`);
  }

  static async createLuxuryVendorVehicle(body) {
    return this.bffRequest('/luxury-cars/admin/vendor-vehicles', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async updateLuxuryVendorVehicle(id, body) {
    return this.bffRequest(`/luxury-cars/admin/vendor-vehicles/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  // ✅ Health Check
  static async getHealth() {
    const query = `
      query GetHealth {
        health {
          status
          timestamp
          database
        }
      }
    `;

    return this.graphqlRequest(query);
  }
}

export default ApiService;